// 内容分割与特殊标题处理工具
import React from 'react';
import { Document, Packer, Paragraph, HeadingLevel, Table, TableRow, TableCell } from 'docx';

import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { marked } from 'marked';
import { message } from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { Spin } from 'antd';
// 注释掉pandoc相关导入，改用浏览器原生功能

// PDF下载锁，确保同时只能有一个PDF在下载
let isPdfDownloading = false;

/**
 * 获取报告状态配置
 * @param {number} themeColor 主题颜色编号
 * @returns {Object} 状态配置对象
 */
export const getReportStatusConfig = (themeColor = 1) => {
    // 主题颜色配置
    return {
        loading: {
            icon: (props) => <Spin className="status-icon loading-icon" {...props} />,
            color: 'rgba(59, 112, 255, 0.1)',
            text: '正在研究',
            className: 'loading'
        },
        success: {
            icon: (props) => <CheckCircleFilled className="status-icon success-icon" {...props} />,
            color: 'rgba(82, 196, 26, 0.1)',
            text: '已完成研究',
            className: 'success'
        },
        error: {
            icon: (props) => <CloseCircleFilled className="status-icon error-icon" {...props} />,
            color: 'rgba(245, 34, 45, 0.1)',
            text: '已停止',
            className: 'error'
        },
        stopped: {
            icon: (props) => <CloseCircleFilled className="status-icon stopped-icon" {...props} />,
            color: 'rgba(245, 34, 45, 0.1)',
            text: '已停止',
            className: 'stopped'
        }
    };
};

/**
 * 获取显示标题
 * @param {string} localTitle 本地标题
 * @param {string} defaultTitle 默认标题
 * @returns {string} 显示标题
 */
export const getDisplayTitle = (localTitle, defaultTitle = '研究报告') => {
    // 如果已经有真实标题（不是默认标题），则始终显示真实标题
    if (localTitle && localTitle !== defaultTitle) {
        return localTitle;
    }
    // 如果没有真实标题，则显示默认标题
    return defaultTitle;
};

/**
 * 检查是否需要加载标题
 * @param {string} status 当前状态
 * @param {string} cardId 卡片ID
 * @param {boolean} hasLoadedFromStorage 是否已从存储加载过
 * @param {string} lastLoadedTitle 上次加载的标题
 * @param {string} localTitle 本地标题
 * @returns {boolean} 是否需要加载标题
 */
export const shouldLoadTitle = (status, cardId, hasLoadedFromStorage, lastLoadedTitle, localTitle) => {
    return status === 'success' && cardId && !hasLoadedFromStorage && !lastLoadedTitle && localTitle === '研究报告';
};

/**
 * 从存储中加载报告标题
 * @param {string} cardId 卡片ID
 * @returns {Object} 包含标题和是否成功的对象
 */
export const loadReportTitle = (cardId) => {
    try {
        // 检查deerflow_reports_data中的completed
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const completedReports = unifiedData.completed || {};
        if (completedReports[cardId]?.title && completedReports[cardId].title !== '研究报告') {
            return {
                title: completedReports[cardId].title,
                success: true
            };
        }

        // 如果没有，检查单独存储的报告数据
        const key = `grt_report_${cardId}`;
        const savedData = JSON.parse(localStorage.getItem(key) || '{}');
        if (savedData.title && savedData.title !== '研究报告') {
            return {
                title: savedData.title,
                success: true
            };
        }

        return { title: null, success: false };
    } catch (error) {
        console.error('获取报告标题失败:', error);
        return { title: null, success: false };
    }
};

/**
 * 获取所有停止的报告
 * @returns {Object} 停止报告对象
 */
export const getStoppedReports = () => {
    try {
        const stoppedReports = localStorage.getItem(STOPPED_REPORTS_KEY);
        return stoppedReports ? JSON.parse(stoppedReports) : {};
    } catch (error) {
        console.error('获取停止报告失败:', error);
        return {};
    }
};

/**
 * 检查是否有用户停止标记
 * @param {string} cardId 卡片ID
 * @returns {boolean} 是否被用户停止
 */
export const checkUserStoppedState = (cardId) => {
    const stoppedReports = getStoppedReports();
    return stoppedReports[cardId] === true;
};

/**
 * 设置用户停止标记
 * @param {string} cardId 卡片ID
 */
export const setUserStoppedState = (cardId) => {
    try {
        const stoppedReports = getStoppedReports();
        stoppedReports[cardId] = true;
        localStorage.setItem(STOPPED_REPORTS_KEY, JSON.stringify(stoppedReports));
    } catch (error) {
        console.error('设置停止状态失败:', error);
    }
};

/**
 * 清除单个报告的停止状态
 * @param {string} cardId 卡片ID
 */
export const clearStoppedState = (cardId) => {
    try {
        const stoppedReports = getStoppedReports();
        delete stoppedReports[cardId];
        localStorage.setItem(STOPPED_REPORTS_KEY, JSON.stringify(stoppedReports));
    } catch (error) {
        console.error('清除停止状态失败:', error);
    }
};

/**
 * 清除所有停止的报告状态
 */
export const clearAllStoppedStates = () => {
    try {
        localStorage.removeItem(STOPPED_REPORTS_KEY);
    } catch (error) {
        console.error('清除所有停止状态失败:', error);
    }
};

/**
 * 批量清除指定报告的停止状态
 * @param {Array<string>} cardIds 卡片ID数组
 */
export const batchClearStoppedStates = (cardIds) => {
    try {
        const stoppedReports = getStoppedReports();
        cardIds.forEach((cardId) => {
            delete stoppedReports[cardId];
        });
        localStorage.setItem(STOPPED_REPORTS_KEY, JSON.stringify(stoppedReports));
    } catch (error) {
        console.error('批量清除停止状态失败:', error);
    }
};

/**
 * 迁移旧的停止状态存储格式到新的统一格式
 * 将分散的 stopped_${cardId} 键合并到 deerflow_stopped_reports 对象中
 */
export const migrateStoppedStates = () => {
    try {
        // 检查是否已经迁移过
        const existingStoppedReports = getStoppedReports();
        const hasExistingData = Object.keys(existingStoppedReports).length > 0;

        // 查找所有旧格式的停止状态
        const oldStoppedKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('stopped_') && key !== STOPPED_REPORTS_KEY) {
                oldStoppedKeys.push(key);
            }
        }

        if (oldStoppedKeys.length === 0) {
            return; // 没有需要迁移的数据
        }

        // 迁移数据
        const stoppedReports = hasExistingData ? existingStoppedReports : {};
        let migrated = false;

        oldStoppedKeys.forEach((key) => {
            const value = localStorage.getItem(key);
            if (value === 'true') {
                // 提取cardId (移除 'stopped_' 前缀)
                const cardId = key.substring('stopped_'.length);
                if (cardId) {
                    stoppedReports[cardId] = true;
                    migrated = true;
                }
            }
            // 删除旧的键
            localStorage.removeItem(key);
        });

        // 保存迁移后的数据
        if (migrated) {
            localStorage.setItem(STOPPED_REPORTS_KEY, JSON.stringify(stoppedReports));
        }
    } catch (error) {
        console.error('迁移停止状态失败:', error);
    }
};

/**
 * 获取所有旧格式的停止状态键（用于清理）
 * @returns {Array<string>} 旧格式的键数组
 */
export const getOldStoppedStateKeys = () => {
    const oldKeys = [];
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('stopped_') && key !== STOPPED_REPORTS_KEY) {
                oldKeys.push(key);
            }
        }
    } catch (error) {
        console.error('获取旧停止状态键失败:', error);
    }
    return oldKeys;
};

/**
 * 清理报告相关的所有本地存储（包括停止状态）
 * @param {string} reportId 报告ID
 */
export const cleanupReportStorage = (reportId) => {
    try {
        // 清理报告内容
        const reportKey = `grt_report_${reportId}`;
        localStorage.removeItem(reportKey);

        // 清理停止状态
        clearStoppedState(reportId);

        // 清理deerflow_reports_data中的记录
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const completedReports = unifiedData.completed || {};
        if (completedReports[reportId]) {
            delete completedReports[reportId];
            unifiedData.completed = completedReports;
            localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));
        }

        // 清理线程映射
        const threadMap = JSON.parse(localStorage.getItem('grt_report_thread_map') || '{}');
        Object.keys(threadMap).forEach((threadId) => {
            if (threadMap[threadId] === reportId) {
                delete threadMap[threadId];
            }
        });
        localStorage.setItem('grt_report_thread_map', JSON.stringify(threadMap));
    } catch (error) {
        console.error('清理报告存储失败:', error);
    }
};

/**
 * 批量清理多个报告的存储
 * @param {Array<string>} reportIds 报告ID数组
 */
export const batchCleanupReportStorage = (reportIds) => {
    try {
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const completedReports = unifiedData.completed || {};
        const threadMap = JSON.parse(localStorage.getItem('grt_report_thread_map') || '{}');

        reportIds.forEach((reportId) => {
            // 清理报告内容
            const reportKey = `grt_report_${reportId}`;
            localStorage.removeItem(reportKey);

            // 清理deerflow_reports_data中的记录
            if (completedReports[reportId]) {
                delete completedReports[reportId];
            }

            // 清理线程映射
            Object.keys(threadMap).forEach((threadId) => {
                if (threadMap[threadId] === reportId) {
                    delete threadMap[threadId];
                }
            });
        });

        // 批量清理停止状态
        batchClearStoppedStates(reportIds);

        // 保存更新后的数据
        unifiedData.completed = completedReports;
        localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));
        localStorage.setItem('grt_report_thread_map', JSON.stringify(threadMap));
    } catch (error) {
        console.error('批量清理报告存储失败:', error);
    }
};

/**
 * 清理所有报告相关的本地存储
 */
export const clearAllReportStorage = () => {
    try {
        // 清理所有报告内容
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('grt_report_')) {
                keys.push(key);
            }
        }
        keys.forEach((key) => localStorage.removeItem(key));

        // 清理主要存储
        localStorage.removeItem('grt_report_thread_map');
        localStorage.removeItem('deerflow_reports_data'); // 🔥 合并后的统一存储键，包含completed和generating
        localStorage.removeItem('grt_history_sessions'); // 🔥 清理不再使用的历史会话存储

        // 清理所有停止状态
        clearAllStoppedStates();

        // 清理其他相关存储
        localStorage.removeItem('last-report-thread-id');
        localStorage.removeItem('last-report-id');
    } catch (error) {
        console.error('清理所有报告存储失败:', error);
    }
};

/**
 * 清理指定会话下的所有本地报告数据
 * @param {string} sessionId 会话ID
 */
export const clearReportsForSession = (sessionId) => {
    if (!sessionId) return;

    try {
        // 清理deerflow_reports_data中该会话的所有报告
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const completedReports = unifiedData.completed || {};
        const generatingReports = unifiedData.generating || {};
        let hasChanges = false;

        // 清理已完成的报告
        Object.keys(completedReports).forEach((reportId) => {
            const report = completedReports[reportId];
            // 检查报告是否属于该会话（使用sessionId或threadId）
            if (report && (report.sessionId === sessionId || report.threadId === sessionId || report.session_id === sessionId)) {
                delete completedReports[reportId];
                hasChanges = true;
            }
        });

        // 清理生成中的报告
        Object.keys(generatingReports).forEach((reportId) => {
            const report = generatingReports[reportId];
            // 检查报告是否属于该会话（使用sessionId或threadId）
            if (report && (report.sessionId === sessionId || report.threadId === sessionId || report.session_id === sessionId)) {
                delete generatingReports[reportId];
                hasChanges = true;
            }
        });

        if (hasChanges) {
            unifiedData.completed = completedReports;
            unifiedData.generating = generatingReports;
            localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));
        }

        // 清理停止状态记录
        const stoppedReports = JSON.parse(localStorage.getItem('deerflow_stopped_reports') || '{}');
        let stoppedHasChanges = false;

        Object.keys(stoppedReports).forEach((reportId) => {
            const report = stoppedReports[reportId];
            if (report && (report.sessionId === sessionId || report.threadId === sessionId || report.session_id === sessionId)) {
                delete stoppedReports[reportId];
                stoppedHasChanges = true;
            }
        });

        if (stoppedHasChanges) {
            localStorage.setItem('deerflow_stopped_reports', JSON.stringify(stoppedReports));
        }

        // 清理线程映射关系
        const threadMap = JSON.parse(localStorage.getItem('grt_report_thread_map') || '{}');
        if (threadMap[sessionId]) {
            delete threadMap[sessionId];
            localStorage.setItem('grt_report_thread_map', JSON.stringify(threadMap));
        }

        // 🔥 继续清理已完成报告记录（使用已有的unifiedData）
        if (!unifiedData.completed) unifiedData.completed = {};
        if (!unifiedData.generating) unifiedData.generating = {};
        let completedHasChanges = false;

        Object.keys(unifiedData.completed).forEach((reportId) => {
            const report = unifiedData.completed[reportId];
            // 检查报告是否属于该会话
            if (report && (report.sessionId === sessionId || report.threadId === sessionId || report.session_id === sessionId)) {
                delete unifiedData.completed[reportId];
                completedHasChanges = true;
            }
        });

        // 同时清理generating中的相关数据
        Object.keys(unifiedData.generating).forEach((reportId) => {
            const report = unifiedData.generating[reportId];
            if (report && (report.sessionId === sessionId || report.threadId === sessionId || report.session_id === sessionId)) {
                delete unifiedData.generating[reportId];
                completedHasChanges = true;
            }
        });

        if (completedHasChanges) {
            localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));
        }

        // 🔥 新增：清理grt_history_sessions中对应的会话记录
        const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
        const updatedHistorySessions = historySessions.filter((session) => {
            // 过滤掉匹配的sessionId
            return session.session_id !== sessionId && session.sessionId !== sessionId && session.id !== sessionId;
        });

        if (updatedHistorySessions.length !== historySessions.length) {
            localStorage.setItem('grt_history_sessions', JSON.stringify(updatedHistorySessions));
        }
    } catch (error) {
        console.error('清理会话报告数据时出错:', error);
    }
};

/**
 * 验证事件目标ID是否匹配
 * @param {Object} detail 事件详情
 * @param {string} cardId 当前卡片ID
 * @returns {boolean} 是否匹配
 */
export const validateEventTarget = (detail, cardId) => {
    if (!detail) return false;
    const eventTargetId = detail.uniqueReportId || detail.reportId;
    return !eventTargetId || eventTargetId === cardId;
};

/**
 * 检查报告的真实完成状态
 * @param {string} cardId 卡片ID
 * @returns {Object} 包含isCompleted和realStatus的对象
 */
export const checkReportCompletionStatus = (cardId) => {
    if (!cardId) {
        return { isCompleted: false, realStatus: 'loading' };
    }

    try {
        // 🔥 优先检查停止状态 - 修复刷新后先显示loading再显示stopped的问题
        const stoppedReports = JSON.parse(localStorage.getItem('deerflow_stopped_reports') || '{}');
        if (stoppedReports[cardId]) {
            return { isCompleted: true, realStatus: 'stopped' };
        }

        // 检查deerflow_reports_data中的completed是否有此报告
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const completedReports = unifiedData.completed || {};
        if (completedReports[cardId]) {
            return { isCompleted: true, realStatus: 'success' };
        }


        // 检查单独存储的报告数据
        const key = `grt_report_${cardId}`;
        const savedData = JSON.parse(localStorage.getItem(key) || '{}');
        if (savedData.isFinal || (savedData.reportData && savedData.title && savedData.title !== '研究报告')) {
            return { isCompleted: true, realStatus: 'success' };
        }

        return { isCompleted: false, realStatus: 'loading' };
    } catch (error) {
        console.error('检查报告完成状态失败:', error);
        return { isCompleted: false, realStatus: 'loading' };
    }
};

/**
 * 初始化报告标题
 * @param {string} title 传入的标题
 * @param {string} status 当前状态
 * @param {string} cardId 卡片ID
 * @param {boolean} wasStopped 是否已停止
 * @returns {string} 初始化后的标题
 */
export const initializeReportTitle = (title, status, cardId, wasStopped) => {
    // 🔥 新增：检查报告的真实完成状态
    const { isCompleted, realStatus } = checkReportCompletionStatus(cardId);

    // 如果报告已完成，使用真实状态而不是传入的status
    const effectiveStatus = wasStopped ? 'stopped' : isCompleted ? realStatus : status;

    // 🔥 优先级1：如果传入了真实标题且状态是success，优先使用传入的标题
    if (effectiveStatus === 'success' && title && title !== '研究报告') {
        return title;
    }

    // 🔥 优先级2：如果报告已完成或状态是success，积极从localStorage加载
    if (cardId && (effectiveStatus === 'success' || effectiveStatus === 'stopped' || isCompleted)) {
        const { title: savedTitle } = loadReportTitle(cardId);
        if (savedTitle && savedTitle !== '研究报告') {
            return savedTitle;
        }
    }

    // 🔥 优先级3：对于loading状态或其他情况，使用默认标题
    return '研究报告';
};

/**
 * 处理特殊标题，修复markdown格式问题
 * @param {string} text 原始文本
 * @returns {string} 处理后的文本
 */
export function processSpecialHeadings(text) {
    if (!text) return '';
    // 第一步：处理列表项后面附带的标题问题
    let processed = text.replace(/(- \[[^\]]+\]\([^)]+\))([#]+\s+[^\n]+)/g, '$1\n\n$2');
    processed = processed.replace(/(- [^\n]+)([#]+\s+[^\n]+)/g, '$1\n\n$2');
    processed = processed.replace(/([^\n])# 问题陈述/g, '$1\n\n# 问题陈述');
    processed = processed.replace(/([^\n])# Problem Statement/g, '$1\n\n# Problem Statement');
    processed = processed.replace(/^References$/gm, '## References');
    processed = processed.replace(/^参考资料$/gm, '## 参考资料');
    processed = processed.replace(/^参考文献$/gm, '## 参考文献');
    processed = processed.replace(/(\S)# ([A-Za-z\u4e00-\u9fa5][A-Za-z\u4e00-\u9fa5\s]+)/g, '$1\n\n# $2');
    processed = processed.replace(/(参考资料[\s\S]*?)# ([^\n]+)/g, '$1\n\n# $2');
    processed = processed.replace(/(参考资料[\s\S]*?)## ([^\n]+)/g, '$1\n\n## $2');
    processed = processed.replace(/(参考文献[\s\S]*?)# ([^\n]+)/g, '$1\n\n# $2');
    processed = processed.replace(/(参考文献[\s\S]*?)## ([^\n]+)/g, '$1\n\n## $2');
    processed = processed.replace(/(References[\s\S]*?)# ([^\n]+)/g, '$1\n\n# $2');
    processed = processed.replace(/(References[\s\S]*?)## ([^\n]+)/g, '$1\n\n## $2');
    processed = processed.replace(/(- \[[^\]]+\]\([^)]+\))([#]+\s+[^\n]+)/g, '$1\n\n$2');
    processed = processed.replace(/^[ \t]*#[ \t]*$/gm, '');
    processed = processed.replace(/^[ \t]*##[ \t]*$/gm, '');
    const lines = processed.split('\n');
    const fixedLines = [];
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.trim().startsWith('- ')) {
            const hashIndex = line.indexOf('#');
            if (hashIndex > 0 && hashIndex > line.indexOf('- ')) {
                const listPart = line.substring(0, hashIndex).trim();
                const headingPart = line.substring(hashIndex).trim();
                if (headingPart.match(/^#+\s+\S+/)) {
                    fixedLines.push(listPart);
                    fixedLines.push('');
                    fixedLines.push(headingPart);
                    continue;
                }
            }
        }
        if (line.includes('# 问题陈述') && !line.trim().startsWith('#')) {
            const parts = line.split('# 问题陈述');
            fixedLines.push(parts[0].trim());
            fixedLines.push('');
            fixedLines.push('# 问题陈述');
            continue;
        }
        if (line.includes('# Problem Statement') && !line.trim().startsWith('#')) {
            const parts = line.split('# Problem Statement');
            fixedLines.push(parts[0].trim());
            fixedLines.push('');
            fixedLines.push('# Problem Statement');
            continue;
        }
        fixedLines.push(line);
    }
    return fixedLines.join('\n');
}

/**
 * 分析内容结构，提取活动内容、报告内容和标题
 * @param {string} content 原始内容
 * @returns {Object} 包含activityContent、reportContent和title的对象
 */
export function analyzeContentStructure(content) {
    if (!content) {
        return {
            activityContent: '',
            reportContent: '',
            title: ''
        };
    }
    const switchTitleMarkIndex = content.indexOf('#切换标题');
    if (switchTitleMarkIndex !== -1) {
        const activityContentText = content.substring(0, switchTitleMarkIndex);
        const lines = content.substring(switchTitleMarkIndex).split('\n');
        const reportLines = [];
        let skipLine = true;
        for (const line of lines) {
            if (skipLine && line.trim() === '#切换标题') {
                skipLine = false;
                continue;
            }
            reportLines.push(line);
        }
        const reportContentText = reportLines.join('\n');
        const processedActivityContent = processSpecialHeadings(activityContentText);
        const processedReportContent = processSpecialHeadings(reportContentText);
        const titleMatch = processedReportContent.match(/^# (.+)/m);
        const title = titleMatch ? titleMatch[1].trim() : '研究报告';
        return {
            activityContent: processedActivityContent,
            reportContent: processedReportContent,
            title
        };
    }
    const titleMatch = content.match(/^# (.+)/m);
    const title = titleMatch ? titleMatch[1].trim() : '研究报告';
    return {
        activityContent: processSpecialHeadings(content),
        reportContent: '',
        title
    };
}

/**
 * 从报告内容中提取重点和段落
 * @param {string} content 报告内容
 * @returns {Object} 包含highlights和sections的对象
 */
export function extractReportHighlights(content) {
    if (!content) return { highlights: [], sections: [] };

    try {
        // 提取所有标题
        const headingRegex = /^#{1,3}\s+(.+?)$/gm;
        const highlights = [];
        const sections = [];
        let match;

        // 处理所有标题
        while ((match = headingRegex.exec(content)) !== null) {
            const title = match[1].trim();
            // 添加到重点
            highlights.push(title);

            // 获取标题的开始位置
            const startPos = match.index;
            // 查找下一个标题的开始位置
            headingRegex.lastIndex = startPos + match[0].length;
            const nextMatch = headingRegex.exec(content);
            const endPos = nextMatch ? nextMatch.index : content.length;
            headingRegex.lastIndex = startPos + match[0].length; // 重置正则表达式的索引

            // 提取此标题下的内容
            const sectionContent = content.substring(startPos + match[0].length, endPos).trim();

            sections.push({
                title,
                content: sectionContent
            });
        }

        return {
            highlights,
            sections
        };
    } catch (error) {
        console.error('提取报告重点出错:', error);
        return { highlights: [], sections: [] };
    }
}

/**
 * 创建报告通知卡片内容
 * @param {Object} options 配置选项
 * @returns {string} 卡片HTML
 */
export function createReportNoticeCard(options = {}) {
    const { title = '研究报告', autoOpen = true, state = 'opened' } = options;

    return `<div data-type="report-notice" data-title="${title}" data-state="${state}" data-auto-open="${autoOpen}"></div>`;
}

// ========== 新增的工具函数 ==========

/**
 * 本地存储的键名前缀
 */
const LOCAL_STORAGE_KEY_PREFIX = 'grt_report_';
const THREAD_TO_REPORT_MAP_KEY = `${LOCAL_STORAGE_KEY_PREFIX}thread_map`;
const STOPPED_REPORTS_KEY = 'deerflow_stopped_reports';

/**
 * 生成唯一的报告ID
 */
export const generateReportId = () => {
    return 'report_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
};

/**
 * 获取或创建报告ID
 * @param {string} threadId 线程ID
 * @param {boolean} isNewReport 是否创建新报告
 * @returns {string|null} 报告ID
 */
export const getOrCreateReportId = (threadId, isNewReport = false) => {
    if (!threadId) return null;

    try {
        const threadMapStr = localStorage.getItem(THREAD_TO_REPORT_MAP_KEY);
        let threadMap = {};

        if (threadMapStr) {
            threadMap = JSON.parse(threadMapStr);
        }

        // 确保threadMap[threadId]是一个数组
        if (!threadMap[threadId]) {
            threadMap[threadId] = [];
        } else if (!Array.isArray(threadMap[threadId])) {
            // 兼容旧格式：如果是字符串(旧格式)，转换为数组
            threadMap[threadId] = [threadMap[threadId]];
        }

        // 如果不是创建新报告且已有关联的reportId，返回最后一个(最新的)
        if (!isNewReport && threadMap[threadId].length > 0) {
            const latestReportId = threadMap[threadId][threadMap[threadId].length - 1];
            return latestReportId;
        }

        // 创建新的reportId并保存映射关系
        const newReportId = generateReportId();
        threadMap[threadId].push(newReportId);
        localStorage.setItem(THREAD_TO_REPORT_MAP_KEY, JSON.stringify(threadMap));

        return newReportId;
    } catch (e) {
        // 出错时仍创建新的，但不保存映射
        return generateReportId();
    }
};

/**
 * 从本地存储加载报告数据
 * @param {string} threadId 线程ID
 * @param {string} specificReportId 特定报告ID
 * @param {string} uniqueReportId 唯一报告ID
 * @returns {Object|null} 报告数据
 */
export const loadReportFromLocalStorage = (threadId, specificReportId = null, uniqueReportId = null) => {
    try {
        // 从deerflow_reports_data集合中获取报告数据
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const completedReports = unifiedData.completed || {};
        const generatingReports = unifiedData.generating || {};

        // 合并两个存储的数据进行查找
        const allReports = { ...completedReports, ...generatingReports };

        // 优先使用uniqueReportId查找
        if (uniqueReportId && allReports[uniqueReportId]) {
            return {
                ...allReports[uniqueReportId],
                activityContent: allReports[uniqueReportId].activityContent,
                reportContent: allReports[uniqueReportId].reportContent,
                reportId: uniqueReportId,
                uniqueReportId: uniqueReportId
            };
        }

        // 如果提供了specificReportId，尝试使用它查找
        if (specificReportId && allReports[specificReportId]) {
            return {
                ...allReports[specificReportId],
                activityContent: allReports[specificReportId].activityContent,
                reportContent: allReports[specificReportId].reportContent,
                reportId: specificReportId,
                uniqueReportId: specificReportId
            };
        }

        // 如果提供了threadId，尝试通过映射关系查找对应的reportId
        if (threadId) {
            try {
                const threadMap = JSON.parse(localStorage.getItem('grt_report_thread_map') || '{}');
                const mappedReportId = threadMap[threadId];

                if (mappedReportId && allReports[mappedReportId]) {
                    return {
                        ...allReports[mappedReportId],
                        activityContent: allReports[mappedReportId].activityContent,
                        reportContent: allReports[mappedReportId].reportContent,
                        reportId: mappedReportId,
                        uniqueReportId: mappedReportId
                    };
                }
            } catch (e) {
                console.error('获取线程映射关系失败', e);
            }
        }

        return null;
    } catch (e) {
        return null;
    }
};

/**
 * 保存报告到本地存储
 * @param {Object} options 保存选项
 */
export const saveReportToLocalStorage = ({ threadId, reportId, uniqueReportId, activityContent, reportContent, title, isFinal = false }) => {
    // 如果没有必要的ID，不执行保存
    if (!threadId || !reportId) {
        return;
    }
    // 提取报告中的标题（如果有的话）
    let extractedTitle = title;
    if (reportContent && typeof reportContent === 'string') {
        const titleMatch = reportContent.match(/^#\s+(.+?)$/m);
        if (titleMatch && titleMatch[1]) {
            extractedTitle = titleMatch[1];
        }
    }

    try {
        // 只使用deerflow_reports_data集合存储报告，不再单独存储
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const finalReportId = uniqueReportId || reportId;

        // 确定保存到哪个部分
        const targetStorage = isFinal ? unifiedData.completed : unifiedData.generating;
        if (!targetStorage) {
            if (isFinal) {
                unifiedData.completed = {};
            } else {
                unifiedData.generating = {};
            }
        }

        // 更新或创建报告数据
        const targetSection = isFinal ? unifiedData.completed : unifiedData.generating;
        targetSection[finalReportId] = {
            threadId,
            sessionId: threadId, // 使用sessionId作为会话标识
            session_id: threadId, // 兼容旧格式
            activityContent: activityContent,
            reportContent: reportContent,
            title: extractedTitle,
            timestamp: Date.now(),
            isOpen: true, // 默认打开状态
            isFinal: isFinal
        };
        // 保存到本地存储
        localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));

        // 确保thread_id和report_id的映射关系存在
        try {
            const threadMap = JSON.parse(localStorage.getItem('grt_report_thread_map') || '{}');
            threadMap[threadId] = finalReportId;
            localStorage.setItem('grt_report_thread_map', JSON.stringify(threadMap));
        } catch (e) {
            console.error('保存报告线程映射关系失败', e);
        }
    } catch (e) {
        console.error('保存报告数据失败', e);
    }
};

/**
 * 从本地存储加载标题
 * @param {string} uniqueReportId 唯一报告ID
 * @returns {string|null} 标题
 */
export const loadTitleFromStorage = (uniqueReportId) => {
    try {
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const completedReports = unifiedData.completed || {};
        const generatingReports = unifiedData.generating || {};

        const reportData = completedReports[uniqueReportId] || generatingReports[uniqueReportId];

        if (reportData) {
            // 优先使用保存的title
            if (reportData.title && reportData.title !== '研究报告') {
                return reportData.title;
            }
            // 如果没有title但有reportContent，从内容中提取
            else if (reportData.reportContent) {
                const titleMatch = reportData.reportContent.match(/^#\s+(.+?)$/m);
                if (titleMatch && titleMatch[1]) {
                    const extractedTitle = titleMatch[1];

                    // 更新回本地存储
                    if (completedReports[uniqueReportId]) {
                        completedReports[uniqueReportId].title = extractedTitle;
                    } else if (generatingReports[uniqueReportId]) {
                        generatingReports[uniqueReportId].title = extractedTitle;
                    }

                    unifiedData.completed = completedReports;
                    unifiedData.generating = generatingReports;
                    localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));

                    return extractedTitle;
                }
            }
        }
    } catch (e) {
        console.error('从本地存储加载标题失败', e);
    }
    return null;
};

/**
 * Markdown转DOCX转换函数
 * @param {string} markdown Markdown内容
 * @param {string} title 文档标题
 * @returns {Promise<Blob>} DOCX文件Blob
 */
export const markdownToDocx = async (markdown, title) => {
    try {
        // 将Markdown解析为行
        const lines = markdown.split('\n');
        const docElements = [];

        // 处理每一行，转换为DOCX文档对象
        lines.forEach((line) => {
            // 简单处理标题
            if (line.startsWith('# ')) {
                docElements.push(
                    new Paragraph({
                        text: line.replace('# ', ''),
                        heading: HeadingLevel.HEADING_1
                    })
                );
            } else if (line.startsWith('## ')) {
                docElements.push(
                    new Paragraph({
                        text: line.replace('## ', ''),
                        heading: HeadingLevel.HEADING_2
                    })
                );
            } else if (line.startsWith('### ')) {
                docElements.push(
                    new Paragraph({
                        text: line.replace('### ', ''),
                        heading: HeadingLevel.HEADING_3
                    })
                );
            } else if (line.trim() !== '') {
                // 普通文本
                docElements.push(
                    new Paragraph({
                        text: line
                    })
                );
            } else {
                // 空行
                docElements.push(new Paragraph({}));
            }
        });

        // 创建文档
        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: docElements
                }
            ]
        });

        // 打包文档为blob
        return await Packer.toBlob(doc);
    } catch (error) {
        console.error('Markdown转DOCX失败:', error);
        message.error('生成DOCX文件失败');
        throw error;
    }
};

/**
 * 智能分页处理函数
 * @param {jsPDF} pdf PDF对象
 * @param {HTMLCanvasElement} canvas 原始canvas
 * @param {string} imgData 图片数据
 * @param {number} imgWidth 图片宽度
 * @param {number} imgHeight 图片高度
 * @param {number} maxHeight 页面最大高度
 * @param {number} margin 页边距
 */
const smartPageBreak = async (pdf, canvas, imgData, imgWidth, imgHeight, maxHeight, margin) => {
    const pageHeight = maxHeight;
    const canvasHeight = canvas.height;
    const scaleFactor = canvasHeight / imgHeight;

    // 🔥 新增：异常检测 - 如果canvas高度异常大，使用带智能断点的动态分页
    if (canvasHeight > 8000) {
        console.warn('⚠️ 检测到异常大的canvas，使用带智能断点的动态分页策略');

        let currentY = 0;
        let pageCount = 0;
        const maxPages = 50; // 🔥 修复：增加到50页，确保长内容能完整输出
        const canvasPageHeight = Math.floor(pageHeight * scaleFactor);

        while (currentY < canvasHeight && pageCount < maxPages) {
            if (pageCount > 0) pdf.addPage();

            // 🔥 智能修复：动态计算页面高度，避免文字被切断
            let canvasCurrentHeight = Math.min(canvasPageHeight, canvasHeight - currentY);

            // 🔥 新增：对非最后一页使用智能断点检测
            if (currentY + canvasCurrentHeight < canvasHeight && pageCount < maxPages - 1) {
                const bufferHeight = Math.floor(canvasPageHeight * 0.1); // 10%的缓冲区
                const searchStart = Math.max(0, canvasCurrentHeight - bufferHeight);
                const searchEnd = canvasCurrentHeight;

                // 寻找最佳断点
                const bestBreakPoint = findBestBreakPoint(canvas, currentY, searchStart, searchEnd);

                // 🔥 新增：处理内容完整性保护
                const relativeBreakPoint = bestBreakPoint - currentY; // 转换为相对位置

                if (relativeBreakPoint <= searchStart) {
                    // 智能断点建议将整个内容块移到下一页
                    const adjustedHeight = searchStart;
                    if (adjustedHeight >= canvasPageHeight * 0.3) {
                        // 至少保留30%的高度
                        canvasCurrentHeight = adjustedHeight;
                    }
                } else if (relativeBreakPoint > searchStart && relativeBreakPoint < searchEnd) {
                    const newHeight = relativeBreakPoint;
                    if (newHeight >= canvasPageHeight * 0.7) {
                        // 至少保留70%的高度
                        canvasCurrentHeight = newHeight;
                    }
                }
            }

            if (canvasCurrentHeight <= 0) break;

            // 创建页面片段
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = canvas.width;
            pageCanvas.height = canvasCurrentHeight;
            const pageCtx = pageCanvas.getContext('2d');

            // 填充白色背景
            pageCtx.fillStyle = '#ffffff';
            pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

            // 绘制内容片段
            pageCtx.drawImage(canvas, 0, currentY, canvas.width, canvasCurrentHeight, 0, 0, canvas.width, canvasCurrentHeight);

            const pageImgData = pageCanvas.toDataURL('image/png', 0.95);
            const pdfPageHeight = canvasCurrentHeight / scaleFactor;
            pdf.addImage(pageImgData, 'PNG', margin, margin, imgWidth, pdfPageHeight);

            // 🔥 关键修复：动态更新下一页的起始位置
            currentY += canvasCurrentHeight;
            pageCount++;
        }
        return;
    }

    let remainingHeight = imgHeight;
    let sourceY = 0;
    let pageCount = 0;

    // 计算每页应该包含的canvas像素高度
    const pixelsPerPage = Math.floor(pageHeight * scaleFactor);

    // 🔥 修复：根据实际内容动态分页，移除硬性页数限制
    while (remainingHeight > 0 && pageCount < 100) {
        // 🔥 修复：提高到最多100页，确保超长内容完整
        if (pageCount > 0) {
            pdf.addPage();
        }

        let currentPageHeight = Math.min(remainingHeight, pageHeight);
        let sourceHeight = Math.floor(currentPageHeight * scaleFactor);

        // 🔥 新增：防止无限小的分页
        if (currentPageHeight < 10) {
            console.warn('⚠️ 检测到异常小的分页高度，强制结束分页');
            break;
        }

        // 🔥 优化：智能断点检测，只在必要时调整
        if (remainingHeight > pageHeight && pageCount < 99) {
            // 🔥 修复：配合100页限制调整
            // 适度的缓冲区，避免过度调整
            const bufferHeight = Math.floor(pageHeight * 0.08); // 减少到8%的缓冲区
            const searchStart = Math.max(0, sourceHeight - Math.floor(bufferHeight * scaleFactor));
            const searchEnd = sourceHeight;

            // 寻找空白区域作为更好的断点
            const bestBreakPoint = findBestBreakPoint(canvas, Math.floor(sourceY), searchStart, searchEnd);

            // 🔥 新增：处理内容完整性保护
            const relativeBreakPoint = bestBreakPoint - Math.floor(sourceY); // 转换为相对位置

            if (relativeBreakPoint <= searchStart) {
                // 智能断点建议将整个内容块移到下一页
                const adjustedSourceHeight = searchStart;
                const adjustedPageHeight = adjustedSourceHeight / scaleFactor;
                if (adjustedPageHeight >= pageHeight * 0.3) {
                    // 至少保留30%的高度
                    sourceHeight = adjustedSourceHeight;
                    currentPageHeight = adjustedPageHeight;
                }
            } else if (relativeBreakPoint > searchStart && relativeBreakPoint < searchEnd) {
                const newSourceHeight = relativeBreakPoint;
                const newPageHeight = newSourceHeight / scaleFactor;

                // 只有当新高度合理时才使用（至少是原高度的70%）
                if (newPageHeight >= pageHeight * 0.7) {
                    sourceHeight = newSourceHeight;
                    currentPageHeight = newPageHeight;
                }
            }
        }

        // 🔥 新增：确保sourceHeight不为0
        if (sourceHeight <= 0) {
            console.warn('⚠️ 检测到无效的sourceHeight，强制设置最小值');
            sourceHeight = Math.min(100, canvasHeight - sourceY);
            currentPageHeight = sourceHeight / scaleFactor;
        }

        // 创建页面片段
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;
        const pageCtx = pageCanvas.getContext('2d');

        // 填充白色背景
        pageCtx.fillStyle = '#ffffff';
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

        // 绘制内容
        pageCtx.drawImage(canvas, 0, Math.floor(sourceY), canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);

        const pageImgData = pageCanvas.toDataURL('image/png', 0.95);
        pdf.addImage(pageImgData, 'PNG', margin, margin, imgWidth, currentPageHeight);

        remainingHeight -= currentPageHeight;

        // 🔥 修复：移除重复的重叠处理，避免内容重复
        sourceY += sourceHeight;

        pageCount++;

        // 🔥 新增：异常退出检测
        if (pageCount > 0 && remainingHeight === imgHeight) {
            console.error('🚨 检测到分页死循环，强制退出');
            break;
        }
    }

    // 🔥 新增：分页完成信息
};

/**
 * 寻找最佳分页断点（智能内容完整性检测）
 * @param {HTMLCanvasElement} canvas 原始canvas
 * @param {number} startY 开始Y坐标
 * @param {number} searchStart 搜索开始位置
 * @param {number} searchEnd 搜索结束位置
 * @returns {number} 最佳断点位置
 */
const findBestBreakPoint = (canvas, startY, searchStart, searchEnd) => {
    const ctx = canvas.getContext('2d');
    const width = canvas.width;

    // 🔥 新增：检测内容块的完整性
    const detectContentBlock = (fromY, toY) => {
        let contentLines = 0;
        let totalLines = toY - fromY;

        for (let y = fromY; y < toY; y++) {
            const imageData = ctx.getImageData(0, startY + y, width, 1);
            const data = imageData.data;

            let hasContent = false;
            for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                const alpha = data[i + 3];

                if (alpha > 10 && (r < 240 || g < 240 || b < 240)) {
                    hasContent = true;
                    break;
                }
            }

            if (hasContent) contentLines++;
        }

        return { contentLines, totalLines, contentRatio: contentLines / totalLines };
    };

    // 从底部向上搜索空白行
    for (let y = searchEnd - 1; y >= searchStart; y--) {
        const imageData = ctx.getImageData(0, startY + y, width, 1);
        const data = imageData.data;

        let isBlankLine = true;
        // 检查这一行是否主要是白色/空白
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const alpha = data[i + 3];

            // 如果不是白色或透明，则不是空白行
            if (alpha > 10 && (r < 240 || g < 240 || b < 240)) {
                isBlankLine = false;
                break;
            }
        }

        if (isBlankLine) {
            // 🔥 新增：找到空白行后，检查下方内容块的完整性
            const contentAfterBlank = detectContentBlock(y + 1, searchEnd);

            // 如果下方内容太少（少于50%或少于3行），说明内容被严重切断
            if (contentAfterBlank.contentRatio < 0.5 || contentAfterBlank.contentLines < 3) {
                // 🔥 关键逻辑：寻找更早的断点，让整个内容块移到下一页
                for (let earlierY = y - 10; earlierY >= searchStart; earlierY--) {
                    const earlierImageData = ctx.getImageData(0, startY + earlierY, width, 1);
                    const earlierData = earlierImageData.data;

                    let isEarlierBlankLine = true;
                    for (let i = 0; i < earlierData.length; i += 4) {
                        const r = earlierData[i];
                        const g = earlierData[i + 1];
                        const b = earlierData[i + 2];
                        const alpha = earlierData[i + 3];

                        if (alpha > 10 && (r < 240 || g < 240 || b < 240)) {
                            isEarlierBlankLine = false;
                            break;
                        }
                    }

                    if (isEarlierBlankLine) {
                        return startY + earlierY;
                    }
                }

                // 🔥 如果找不到更早的空白行，直接建议将整个内容块移到下一页
                return startY + searchStart - 1; // 返回搜索区域开始前的位置
            }

            return startY + y;
        }
    }

    // 🔥 新增：如果没找到空白行，检查整个区域的内容密度
    const wholeAreaContent = detectContentBlock(searchStart, searchEnd);
    if (wholeAreaContent.contentRatio < 0.3) {
        return startY + searchStart - 1; // 返回搜索区域开始前的位置，让内容完整移到下一页
    }

    // 如果没找到合适的断点，返回原始位置
    return searchEnd;
};

/**
 * 优化HTML结构以改善PDF分页效果
 * @param {string} htmlContent 原始HTML内容
 * @returns {string} 优化后的HTML内容
 */
const optimizeHtmlForPdf = (htmlContent) => {
    // 为表格添加分页控制类
    htmlContent = htmlContent.replace(/<table/g, '<table class="page-break-avoid"');

    // 为代码块添加分页控制类
    htmlContent = htmlContent.replace(/<pre/g, '<pre class="page-break-avoid"');

    // 为引用块添加分页控制类
    htmlContent = htmlContent.replace(/<blockquote/g, '<blockquote class="page-break-avoid"');

    // 为图片添加分页控制类
    htmlContent = htmlContent.replace(/<img/g, '<img class="page-break-avoid"');

    // 为列表添加分页控制类
    htmlContent = htmlContent.replace(/<ul/g, '<ul class="page-break-avoid"');
    htmlContent = htmlContent.replace(/<ol/g, '<ol class="page-break-avoid"');

    // 在标题前添加适当的分页控制
    htmlContent = htmlContent.replace(/<h([1-6])/g, (match, level) => {
        // h1和h2在前面添加更多空间，避免孤立在页面底部
        if (level <= 2) {
            return `<h${level} style="margin-top: 30pt;"`;
        }
        return match;
    });

    // 为长段落添加分页控制
    htmlContent = htmlContent.replace(/<p>/g, '<p class="page-break-avoid">');

    return htmlContent;
};

/**
 * 简化的Markdown转PDF转换函数
 * @param {string} markdown Markdown内容
 * @param {string} title 文档标题
 * @param {Object} options PDF生成选项
 * @returns {Promise<Blob>} PDF文件Blob
 */
export const markdownToPdf = async (markdown, title, options = {}) => {
    try {
        // 确保markdown是字符串格式
        if (typeof markdown !== 'string') {
            console.warn('markdown不是字符串，尝试转换:', typeof markdown, markdown);
            if (markdown && typeof markdown === 'object') {
                markdown = JSON.stringify(markdown, null, 2);
            } else {
                markdown = String(markdown || '');
            }
        }

        // 添加标题到markdown内容
        let processedMarkdown = markdown;
        if (title && title !== '研究报告') {
            if (!processedMarkdown.trim().startsWith('# ')) {
                processedMarkdown = `# ${title}\n\n${processedMarkdown}`;
            }
        }

        // 创建HTML内容
        let htmlContent;
        try {
            htmlContent = marked.parse(processedMarkdown);
        } catch (error) {
            console.error('Markdown解析失败:', error);
            // 回退：直接使用原始文本，手动转换换行
            htmlContent = processedMarkdown.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>').replace(/^/, '<p>').replace(/$/, '</p>');
        }

        // 确保htmlContent不为空
        if (!htmlContent || htmlContent.trim() === '') {
            htmlContent = `<p>${processedMarkdown || '内容为空'}</p>`;
        }

        // 优化HTML结构，添加分页控制
        htmlContent = optimizeHtmlForPdf(htmlContent);

        // 🔥 修复：创建对html2canvas友好的临时容器
        const container = document.createElement('div');
        container.className = 'html2canvas-container'; // 添加类名便于识别
        container.style.cssText = `
      position: fixed !important;
      left: -10000px !important;
      top: -10000px !important;
      width: 794px !important;
      max-width: 794px !important;
      padding: 60px !important;
      background: white !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
      font-size: 14px !important;
      line-height: 1.6 !important;
      color: #333 !important;
      box-sizing: border-box !important;
      z-index: -10000 !important;
      min-height: 200px !important;
      max-height: 50000px !important; /* 🔥 修复：增加高度限制，支持超长内容 */
      pointer-events: none !important;
      overflow: hidden !important;
      overflow-wrap: break-word !important;
      word-wrap: break-word !important;
      word-break: break-word !important;
    `;

        container.innerHTML = `
      <style>
        .html2canvas-container { opacity: 1 !important; visibility: visible !important; }
        .html2canvas-container * { opacity: 1 !important; visibility: visible !important; }
        .html2canvas-container .content { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        .html2canvas-container .content h1 { 
          font-size: 24pt; margin: 20pt 0 16pt 0; font-weight: bold; color: #2c3e50 !important;
          page-break-before: auto; page-break-after: avoid; page-break-inside: avoid;
        }
        .html2canvas-container .content h2 { 
          font-size: 20pt; margin: 18pt 0 14pt 0; font-weight: bold; color: #34495e !important;
          page-break-before: auto; page-break-after: avoid; page-break-inside: avoid;
        }
        .html2canvas-container .content h3 { 
          font-size: 16pt; margin: 16pt 0 12pt 0; font-weight: bold; color: #34495e !important;
          page-break-before: auto; page-break-after: avoid; page-break-inside: avoid;
        }
        .html2canvas-container .content h4, .html2canvas-container .content h5, .html2canvas-container .content h6 { 
          font-size: 14pt; margin: 14pt 0 10pt 0; font-weight: bold; color: #34495e !important;
          page-break-before: auto; page-break-after: avoid; page-break-inside: avoid;
        }
        .html2canvas-container .content p { 
          margin: 0 0 12pt 0; color: #333 !important;
          page-break-inside: avoid; orphans: 2; widows: 2;
        }
        .html2canvas-container .content ul, .html2canvas-container .content ol { 
          margin: 8pt 0; padding-left: 20pt; color: #333 !important;
          page-break-inside: avoid;
        }
        .html2canvas-container .content li { 
          margin: 4pt 0; color: #333 !important;
          page-break-inside: avoid;
        }
        .html2canvas-container .content pre { 
          background: #f8f9fa; padding: 12pt; border-radius: 4pt; margin: 12pt 0; 
          border-left: 4pt solid #007acc; color: #333 !important;
          page-break-inside: avoid; page-break-before: auto; page-break-after: auto;
        }
        .html2canvas-container .content code { 
          font-family: 'Consolas', monospace; font-size: 10pt; background: #f1f2f6; 
          padding: 2pt 4pt; border-radius: 2pt; color: #333 !important;
        }
        .html2canvas-container .content pre code { background: none; padding: 0; color: #333 !important; }
        .html2canvas-container .content blockquote { 
          margin: 12pt 0; padding: 8pt 16pt; border-left: 4pt solid #ddd; 
          background: #f9f9f9; font-style: italic; color: #333 !important;
          page-break-inside: avoid;
        }
        .html2canvas-container .content table { 
          width: 100%; border-collapse: collapse; margin: 12pt 0;
          page-break-inside: avoid; page-break-before: auto; page-break-after: auto;
        }
        .html2canvas-container .content th, .html2canvas-container .content td { 
          padding: 8pt 12pt; border: 1pt solid #ddd; color: #333 !important;
        }
        .html2canvas-container .content th { background: #f5f5f5; font-weight: bold; }
        .html2canvas-container .content img { 
          max-width: 100%; height: auto;
          page-break-inside: avoid; page-break-before: auto; page-break-after: auto;
        }
        /* 添加分页控制类 */
        .page-break-before { page-break-before: always !important; }
        .page-break-after { page-break-after: always !important; }
        .page-break-avoid { page-break-inside: avoid !important; }
        .content { opacity: 1 !important; visibility: visible !important; color: #333 !important; }
      </style>
      <div class="content">${htmlContent}</div>
    `;

        document.body.appendChild(container);

        // 🔥 修复：增加等待时间确保DOM完全渲染
        await new Promise((resolve) => setTimeout(resolve, 200));

        // 🔥 新增：强制确保容器和内容可见
        container.style.opacity = '1';
        container.style.visibility = 'visible';
        const contentDiv = container.querySelector('.content');
        if (contentDiv) {
            contentDiv.style.opacity = '1';
            contentDiv.style.visibility = 'visible';
            contentDiv.style.color = '#333';
        }

        // 强制重绘
        void container.offsetHeight;
        await new Promise((resolve) => setTimeout(resolve, 100));

        // 🔥 修复：提高内容高度限制，减少截断
        if (container.scrollHeight > 50000) {
            console.warn('⚠️ 内容高度超出限制，进行截断处理');
            container.style.height = '50000px';
            container.style.overflow = 'hidden';
        }

        // 🔥 修复：优化html2canvas配置，确保能捕获内容
        // 检测Safari浏览器
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        const canvas = await html2canvas(container, {
            scale: isSafari ? 1.5 : 2, // Safari使用较低的scale避免性能问题
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: container.offsetWidth,
            height: Math.min(container.scrollHeight, 50000), // 🔥 修复：提高捕获高度限制
            logging: true, // 启用日志帮助调试
            removeContainer: false,
            windowWidth: container.offsetWidth,
            windowHeight: Math.min(container.scrollHeight, 50000), // 🔥 修复：提高窗口高度限制
            x: 0,
            y: 0,
            scrollX: 0,
            scrollY: 0,
            foreignObjectRendering: false, // 禁用可能导致问题的外来对象渲染
            // Safari特有优化
            ...(isSafari && {
                canvas: null, // 让html2canvas自己创建canvas
                letterRendering: true, // 改善文本渲染
                imageTimeout: 15000, // 增加图片加载超时时间
                onclone: function (clonedDoc) {
                    // 确保克隆文档中的字体加载完成
                    const style = clonedDoc.createElement('style');
                    style.textContent = `
            * { 
              -webkit-font-smoothing: antialiased !important; 
              -moz-osx-font-smoothing: grayscale !important; 
            }
          `;
                    clonedDoc.head.appendChild(style);
                }
            })
        });

        // 🔥 确保及时清理临时容器
        try {
            document.body.removeChild(container);
        } catch (e) {
            console.warn('清理临时容器失败:', e);
        }

        // 创建PDF
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const imgData = canvas.toDataURL('image/png', 0.95);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        // 计算适合的尺寸
        const margin = 20;
        const maxWidth = pdfWidth - 2 * margin;
        const maxHeight = pdfHeight - 2 * margin;

        // 计算实际尺寸
        const imgAspect = canvas.height / canvas.width;
        let imgWidth = maxWidth;
        let imgHeight = imgWidth * imgAspect;

        // 如果高度超出页面，需要分页
        if (imgHeight <= maxHeight) {
            // 单页
            pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
        } else {
            // 🔥 新增：更精确的页数计算，确保内容完整渲染
            const estimatedPages = Math.ceil(imgHeight / maxHeight);
            const actualContentHeight = canvas.height / (canvas.height / imgHeight); // 实际内容高度
            const precisePages = Math.ceil(actualContentHeight / maxHeight);
            const finalPages = Math.max(estimatedPages, precisePages);

            // 🔥 修复：降低简化分页的触发阈值，避免智能分页产生大间隔和文字切断
            if (finalPages > 3 || canvas.height > 5000) {
                console.warn('⚠️ 检测到可能的异常内容，使用简化分页策略，最终页数:', finalPages);

                // 🔥 修复：根据实际内容动态确定页数，移除硬性限制
                const actualPages = Math.min(100, finalPages); // 🔥 修复：提高到最多100页，确保超长内容完整渲染
                const pageHeightInPdf = maxHeight; // PDF页面的实际高度

                // 🔥 修复：使用更准确的分页高度计算，添加安全边距
                const scaleFactor = canvas.height / imgHeight;
                const canvasPageHeight = Math.floor(pageHeightInPdf * scaleFactor * 0.95); // 添加5%的安全边距

                let currentY = 0;
                let pageIndex = 0;
                const maxPossiblePages = 100; // 设置一个合理的最大页数限制

                while (currentY < canvas.height && pageIndex < maxPossiblePages) {
                    if (pageIndex > 0) pdf.addPage();

                    // 🔥 智能修复：使用动态分页和智能断点，避免文字被切断
                    let canvasCurrentHeight = Math.min(canvasPageHeight, canvas.height - currentY);

                    // 🔥 新增：对非最后一页使用智能断点检测（除了最后一页，都使用智能断点）
                    if (currentY + canvasCurrentHeight < canvas.height) {
                        const bufferHeight = Math.floor(canvasPageHeight * 0.1); // 10%的缓冲区
                        const searchStart = Math.max(0, canvasCurrentHeight - bufferHeight);
                        const searchEnd = canvasCurrentHeight;

                        // 寻找最佳断点
                        const bestBreakPoint = findBestBreakPoint(canvas, currentY, searchStart, searchEnd);

                        // 🔥 新增：处理内容完整性保护
                        const relativeBreakPoint = bestBreakPoint - currentY; // 转换为相对位置

                        if (relativeBreakPoint <= searchStart) {
                            // 智能断点建议将整个内容块移到下一页
                            const adjustedHeight = searchStart;
                            if (adjustedHeight >= canvasPageHeight * 0.3) {
                                // 至少保留30%的高度
                                canvasCurrentHeight = adjustedHeight;
                            }
                        } else if (relativeBreakPoint > searchStart && relativeBreakPoint < searchEnd) {
                            const newHeight = relativeBreakPoint;
                            if (newHeight >= canvasPageHeight * 0.7) {
                                // 至少保留70%的高度
                                canvasCurrentHeight = newHeight;
                            }
                        }
                    }

                    // 🔥 修复：智能处理剩余内容
                    const remainingHeight = canvas.height - currentY;
                    if (remainingHeight <= canvasPageHeight) {
                        // 剩余内容可以放在当前页
                        canvasCurrentHeight = remainingHeight;
                    }

                    // 如果没有更多内容，跳出循环
                    if (canvasCurrentHeight <= 0 || currentY >= canvas.height) break;

                    // 创建当前页的canvas片段
                    const pageCanvas = document.createElement('canvas');
                    pageCanvas.width = canvas.width;
                    pageCanvas.height = canvasCurrentHeight;
                    const pageCtx = pageCanvas.getContext('2d');

                    // 填充白色背景
                    pageCtx.fillStyle = '#ffffff';
                    pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

                    // 绘制对应的内容片段
                    pageCtx.drawImage(canvas, 0, currentY, canvas.width, canvasCurrentHeight, 0, 0, canvas.width, canvasCurrentHeight);

                    // 转换为图片并添加到PDF
                    const pageImgData = pageCanvas.toDataURL('image/png', 0.95);
                    const pdfPageHeight = canvasCurrentHeight / scaleFactor;
                    pdf.addImage(pageImgData, 'PNG', margin, margin, imgWidth, pdfPageHeight);

                    // 🔥 关键修复：动态更新下一页的起始位置
                    currentY += canvasCurrentHeight;
                    pageIndex++;
                }
            } else {
                // 正常的智能分页处理
                await smartPageBreak(pdf, canvas, imgData, imgWidth, imgHeight, maxHeight, margin);
            }
        }

        return pdf.output('blob');
    } catch (error) {
        console.error('PDF生成失败:', error);
        throw error;
    }
};

/**
 * 下载文件
 * @param {string} content 文件内容
 * @param {string} filename 文件名
 * @param {string} mimeType MIME类型
 */
export const downloadFile = (content, filename, mimeType = 'text/plain') => {
    const element = document.createElement('a');
    const file = new Blob([content], { type: mimeType });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
};

/**
 * 下载Blob文件
 * @param {Blob} blob 文件Blob
 * @param {string} filename 文件名
 */
export const downloadBlob = (blob, filename) => {
    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
};

/**
 * 计算侧边栏的响应式样式
 * @param {Object} windowSize 窗口大小对象 {width, height}
 * @param {boolean} sidebarOpen 侧边栏是否打开
 * @returns {Object} 侧边栏样式对象
 */
export const getSidebarStyle = (windowSize, sidebarOpen) => {
    const { width, height } = windowSize;

    // 根据样式文件，左侧导航栏宽度为260px
    const leftNavWidth = width <= 768 ? 0 : 260; // 小屏幕时导航栏通常隐藏

    // 计算主聊天区域的最小宽度（保证聊天区域有足够空间）
    const minChatWidth = width <= 768 ? 280 : 400;

    // 计算边距（左右各24px）
    const margins = 48; // 24px * 2

    // 计算可用宽度：总宽度 - 导航栏宽度 - 主聊天最小宽度 - 边距
    const availableWidth = width - leftNavWidth - minChatWidth - margins;

    // 添加安全检查：如果计算出的可用宽度为负数或太小，则使用最小值
    const safeAvailableWidth = Math.max(250, availableWidth);

    // 设置侧边栏的最大和最小宽度
    const minSidebarWidth = 300;
    const maxSidebarWidth = 700;

    // 计算实际侧边栏宽度
    let sidebarWidth;

    if (width <= 768) {
        // 小屏幕：占满屏幕减去边距，但不能超过屏幕宽度的80%
        sidebarWidth = Math.min(width - 48, width * 0.8); // 24px * 2
    } else {
        // 大屏幕时的计算
        // 检测可能的缩放情况：当屏幕宽度异常大时，可能是缩放导致的
        const isLikelyZoomedOut = width > 2000; // 可能是缩放缩小
        const isLikelyZoomedIn = width < 1200; // 可能是缩放放大

        if (isLikelyZoomedOut) {
            // 缩放缩小时（如50%），侧边栏应该占更大比例以保持视觉平衡
            let targetRatio;
            if (width >= 3000) {
                targetRatio = 0.45; // 45%
            } else if (width >= 2400) {
                targetRatio = 0.42; // 42%
            } else {
                targetRatio = 0.38; // 38%
            }

            const targetWidth = width * targetRatio;
            sidebarWidth = Math.max(minSidebarWidth, Math.min(targetWidth, 900)); // 增加最大宽度到900px
        } else if (isLikelyZoomedIn) {
            // 缩放放大时，使用更保守的宽度
            sidebarWidth = Math.max(250, Math.min(minSidebarWidth, safeAvailableWidth));
        } else {
            // 正常缩放（100%），使用原来的逻辑
            if (safeAvailableWidth < minSidebarWidth) {
                sidebarWidth = Math.max(250, Math.min(minSidebarWidth, safeAvailableWidth));
            } else if (safeAvailableWidth > maxSidebarWidth) {
                sidebarWidth = maxSidebarWidth;
            } else {
                sidebarWidth = safeAvailableWidth;
            }
        }

        // 最终安全检查：确保不超过屏幕宽度的60%（放宽限制）
        sidebarWidth = Math.min(sidebarWidth, width * 0.6);
    }

    // 计算侧边栏高度
    const topBottomMargins = 48; // 上下边距 24px * 2
    const availableHeight = height - topBottomMargins;
    const maxSidebarHeight = 852;
    const minSidebarHeight = 400;

    // 高度始终为视窗高度减去上下边距（48px）
    let sidebarHeight = availableHeight;

    // 计算主聊天区域需要让出的空间（侧边栏宽度 + 左右边距）
    const sidebarSpace = sidebarWidth + 48; // 侧边栏宽度 + 左右边距(24px * 2)

    // 动态设置CSS变量，用于控制主聊天区域的边距
    if (sidebarOpen) {
        document.documentElement.style.setProperty('--sidebar-space', `${sidebarSpace}px`);
    } else {
        document.documentElement.style.setProperty('--sidebar-space', '0px');
    }

    return {
        position: 'fixed',
        top: '24px',
        right: '24px',
        transform: 'none',
        width: `${sidebarWidth}px`,
        height: `${sidebarHeight}px`,
        maxHeight: `${sidebarHeight}px`,
        background: '#f7f9fb', // 与主聊天区域背景色保持一致
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        borderRadius: '16px',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };
};
/**
 * 全局定时器管理
 */
let globalPollingTimer = null;
let globalActiveReportId = null;

/**
 * 全局定时器清理函数
 */
export const clearGlobalTimer = () => {
    if (globalPollingTimer) {
        clearInterval(globalPollingTimer);
        globalPollingTimer = null;
    }
    globalActiveReportId = null;
};

/**
 * 全局定时器设置函数
 * @param {string} reportId 报告ID
 * @param {Function} callback 回调函数
 * @param {number} interval 间隔时间
 */
export const setGlobalTimer = (reportId, callback, interval = 5000) => {
    // 如果已经有相同reportId的定时器在运行，直接返回
    if (globalActiveReportId === reportId && globalPollingTimer) {
        return;
    }

    // 清理旧的定时器
    clearGlobalTimer();

    // 设置新的定时器
    globalActiveReportId = reportId;
    globalPollingTimer = setInterval(callback, interval);
};

/**
 * 报告缓存管理类
 */
export class ReportCache {
    constructor() {
        this.cache = {};
    }

    /**
     * 获取报告缓存键
     * @param {string} threadId 线程ID
     * @param {string} reportId 报告ID
     * @returns {string} 缓存键
     */
    getCacheKey(threadId, reportId) {
        return `${threadId}_${reportId}`;
    }

    /**
     * 获取缓存数据
     * @param {string} key 缓存键
     * @returns {Object} 缓存数据
     */
    getCache(key) {
        if (!this.cache[key]) {
            this.cache[key] = {
                activityContent: '',
                reportContent: '',
                isFinal: false,
                timestamp: Date.now()
            };
        }
        return this.cache[key];
    }

    /**
     * 更新缓存
     * @param {string} key 缓存键
     * @param {string} activityContent 活动内容
     * @param {string} reportContent 报告内容
     * @param {boolean} isFinal 是否最终版本
     */
    updateCache(key, activityContent, reportContent, isFinal = null) {
        const cache = this.getCache(key);
        cache.activityContent = activityContent || cache.activityContent;
        cache.reportContent = reportContent || cache.reportContent;
        if (isFinal !== null) {
            cache.isFinal = isFinal;
        }
        cache.timestamp = Date.now();
    }

    /**
     * 清除缓存
     * @param {string} key 缓存键
     */
    clearCache(key) {
        delete this.cache[key];
    }
}

/**
 * 滚动位置管理工具
 */
export const scrollPositionManager = {
    /**
     * 保存滚动位置
     * @param {HTMLElement} element 滚动元素
     */
    saveScrollPosition(element) {
        if (element) {
            const scrollTop = element.scrollTop;
            const scrollHeight = element.scrollHeight;
            const clientHeight = element.clientHeight;

            element.dataset.lastScrollTop = scrollTop;
            element.dataset.lastScrollHeight = scrollHeight;
            element.dataset.lastClientHeight = clientHeight;
        }
    },

    /**
     * 恢复滚动位置
     * @param {HTMLElement} element 滚动元素
     */
    restoreScrollPosition(element) {
        if (element && element.dataset.lastScrollTop !== undefined) {
            const lastScrollTop = parseInt(element.dataset.lastScrollTop);
            const lastScrollHeight = parseInt(element.dataset.lastScrollHeight);
            const currentScrollHeight = element.scrollHeight;

            if (currentScrollHeight !== lastScrollHeight) {
                const heightDiff = currentScrollHeight - lastScrollHeight;
                if (heightDiff > 0) {
                    const wasNearBottom = lastScrollTop + element.clientHeight + 100 >= lastScrollHeight;
                    if (wasNearBottom) {
                        element.scrollTop = currentScrollHeight - element.clientHeight;
                    } else {
                        element.scrollTop = lastScrollTop + heightDiff;
                    }
                } else {
                    element.scrollTop = lastScrollTop;
                }
            } else {
                element.scrollTop = lastScrollTop;
            }
        }
    }
};

/**
 * 内容更新安全检查工具
 */
export const contentUpdateValidator = {
    /**
     * 检查内容是否需要更新
     * @param {Object} params 参数对象
     * @returns {boolean} 是否需要更新
     */
    shouldUpdateContent(params) {
        const {
            newActivityContent,
            newReportContent,
            currentActivityContent,
            currentReportContent,
            isFromCache,
            timeSinceLastUpdate,
            currentReportKey,
            previousReportKey
        } = params;

        // 检查是否为当前报告的内容
        if (!isFromCache && previousReportKey && previousReportKey !== currentReportKey) {
            return false;
        }

        // 防抖检查
        if (timeSinceLastUpdate < 300 && !isFromCache) {
            return false;
        }

        // 内容重复检查
        const currentActivityLength = currentActivityContent?.length || 0;
        const currentReportLength = currentReportContent?.length || 0;

        if (
            currentActivityLength === newActivityContent?.length &&
            currentReportLength === newReportContent?.length &&
            currentActivityLength > 0 &&
            !isFromCache
        ) {
            return false;
        }

        return true;
    },

    /**
     * 检查是否应该保护历史报告
     * @param {Object} params 参数对象
     * @returns {boolean} 是否应该保护
     */
    shouldProtectHistoricalReport(params) {
        const { currentReportId, activeReportCardId, isFromCache, hasExistingContent } = params;

        const isCurrentActiveReport = !activeReportCardId || currentReportId === activeReportCardId;

        return !isCurrentActiveReport && !isFromCache && hasExistingContent && activeReportCardId && currentReportId !== activeReportCardId;
    }
};

/**
 * 骨架屏组件配置
 * @param {string} themeColor 主题颜色
 * @returns {Object} 骨架屏配置
 */
export const getSkeletonConfig = (themeColor) => ({
    style: { '--theme-color': themeColor },
    className: 'skeleton-loader'
});

/**
 * 报告管理器类 - 管理报告卡片和侧边栏的状态
 */
export class ReportManager {
    constructor() {
        this.reportNoticeStateUpdaters = {};
        this.generatingReports = {};
        this.activeReportCardId = null;
        this.sidebarOpen = false;
        this.userManualClosed = false;
        this.reportDataRef = {
            threadId: '',
            reportId: null,
            uniqueReportId: null,
            activityData: '',
            reportContent: '',
            title: '研究报告',
            isFinal: false,
            timestamp: 0
        };
    }

    /**
     * 初始化报告管理器
     * @param {Object} config 配置对象
     */
    init(config = {}) {
        this.setStateUpdaters = config.setStateUpdaters || {};
        this.eventCallbacks = config.eventCallbacks || {};
    }

    /**
     * 设置状态更新函数
     * @param {Object} stateUpdaters 状态更新函数集合
     */
    setStateUpdaters(stateUpdaters) {
        this.setStateUpdaters = stateUpdaters;
    }

    /**
     * 处理侧边栏打开
     * @param {string} threadId 线程ID
     * @param {string} reportId 报告ID
     * @param {string} uniqueReportId 唯一报告ID
     * @param {Object} options 额外选项
     */
    handleSidebarOpen = async (threadId, reportId, uniqueReportId = null, options = {}) => {
        const {
            sessionId,
            triggerGlobalPollingCheck,
            setSidebarOpen,
            setActiveReportId,
            setActiveReportCardId,
            setUserManualClosed,
            setSidebarRenderKey,
            setReportData,
            handleSidebarOpenFromFlowData
        } = options;

        if (handleSidebarOpenFromFlowData) {
            return handleSidebarOpenFromFlowData({
                threadId,
                reportId,
                uniqueReportId,
                sessionId,
                reportDataRef: this.reportDataRef,
                triggerGlobalPollingCheck,
                setSidebarOpen,
                setActiveReportId,
                setActiveReportCardId,
                setUserManualClosed,
                setSidebarRenderKey,
                setReportData
            });
        }

        // 默认处理逻辑
        this.sidebarOpen = true;
        this.activeReportCardId = uniqueReportId || reportId;
        this.userManualClosed = false;

        // 保存选中的报告ID到localStorage（对象形式，支持多个报告）
        if ((uniqueReportId || reportId) && threadId) {
            try {
                const selectedReports = JSON.parse(localStorage.getItem('selected_report_id') || '{}');
                selectedReports[threadId] = uniqueReportId || reportId;
                localStorage.setItem('selected_report_id', JSON.stringify(selectedReports));
            } catch (error) {
                console.warn('保存选中报告ID失败:', error);
            }
        }

        // 调用回调函数
        if (setSidebarOpen) setSidebarOpen(true);
        if (setActiveReportCardId) setActiveReportCardId(uniqueReportId || reportId);
        if (setUserManualClosed) setUserManualClosed(false);

        return Promise.resolve();
    };

    /**
     * 处理侧边栏关闭
     * @param {string} reportId 报告ID
     * @param {Object} options 额外选项
     */
    handleSidebarClose = (reportId, options = {}) => {
        const { setSidebarOpen, setUserManualClosed, setActiveReportCardId, threadId } = options;

        this.sidebarOpen = false;
        this.userManualClosed = true;
        this.activeReportCardId = null;

        // 清除CSS变量，恢复主聊天区域宽度
        document.documentElement.style.removeProperty('--sidebar-space');

        // 清理保存的选中报告ID（对象形式，只清理当前threadId）
        try {
            if (threadId) {
                const selectedReports = JSON.parse(localStorage.getItem('selected_report_id') || '{}');
                delete selectedReports[threadId];
                if (Object.keys(selectedReports).length === 0) {
                    localStorage.removeItem('selected_report_id');
                } else {
                    localStorage.setItem('selected_report_id', JSON.stringify(selectedReports));
                }
            } else {
                // 向后兼容：如果没有threadId，清理全部
                localStorage.removeItem('selected_report_id');
            }
        } catch (error) {
            console.warn('清理选中报告ID失败:', error);
        }

        // 直接更新 deerflow_reports_data 中的状态
        if (reportId) {
            try {
                const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
                const completedReports = unifiedData.completed || {};
                const generatingReports = unifiedData.generating || {};

                // 检查在哪个存储中找到报告
                let foundInCompleted = completedReports[reportId];
                let foundInGenerating = generatingReports[reportId];

                if (foundInCompleted || foundInGenerating) {
                    // 更新报告的打开状态
                    if (foundInCompleted) {
                        completedReports[reportId].isOpen = false;
                    } else if (foundInGenerating) {
                        generatingReports[reportId].isOpen = false;
                    }

                    // 保存回本地存储
                    unifiedData.completed = completedReports;
                    unifiedData.generating = generatingReports;
                    localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));

                    // 触发自定义事件，通知卡片状态变化
                    window.dispatchEvent(
                        new CustomEvent('report-state-change', {
                            detail: {
                                reportId,
                                isOpen: false
                            }
                        })
                    );
                }
            } catch (e) {
                console.error('更新报告状态失败', e);
            }
        }

        // 更新卡片状态 - 使用reportId匹配具体卡片
        if (reportId && this.reportNoticeStateUpdaters) {
            const updateFn = this.reportNoticeStateUpdaters[`setIsOpen_${reportId}`];
            if (typeof updateFn === 'function') {
                setTimeout(() => {
                    updateFn(false);
                }, 0);
            }
        }

        // 调用回调函数
        if (setSidebarOpen) setSidebarOpen(false);
        if (setUserManualClosed) setUserManualClosed(true);
        if (setActiveReportCardId) setActiveReportCardId(null);
    };

    /**
     * 检查报告状态
     * @param {Object} originMsgMapRef 原始消息映射引用
     * @param {string} sessionId 会话ID
     * @param {Function} setGeneratingReports 设置生成报告状态的函数
     * @param {Function} triggerGlobalPollingCheck 触发全局轮询检查的函数
     */
    checkReportsStatus = async (originMsgMapRef, sessionId, setGeneratingReports, triggerGlobalPollingCheck) => {
        try {
            const { checkDeerFlowReportsStatus } = await import('../../../utils/Page/flowData');
            await checkDeerFlowReportsStatus(originMsgMapRef, sessionId, setGeneratingReports, this.reportDataRef, triggerGlobalPollingCheck);
        } catch (error) {
            console.error('检查报告状态失败:', error);
        }
    };

    /**
     * 处理报告内容更新事件
     * @param {CustomEvent} event 报告内容更新事件
     * @param {Object} context 上下文对象
     */
    handleReportContentUpdate = (event, context = {}) => {
        // 使用工具函数创建处理器并执行
        const handler = createReportContentUpdateHandler({
            activeReportCardId: this.activeReportCardId,
            reportDataRef: { current: this.reportDataRef },
            sidebarOpen: this.sidebarOpen,
            userManualClosed: this.userManualClosed,
            reportNoticeStateUpdaters: this.reportNoticeStateUpdaters,
            ...context
        });

        return handler(event);
    };

    /**
     * 更新报告数据
     * @param {string} reportId 报告ID
     * @param {Object} data 报告数据
     */
    updateReportData = (reportId, data) => {
        // 检查ID匹配
        if (reportId === this.activeReportCardId || this.reportDataRef?.uniqueReportId === reportId || this.reportDataRef?.reportId === reportId) {
            this.reportDataRef = {
                ...this.reportDataRef,
                ...data
            };
        }
    };

    /**
     * 清理报告缓存
     * @param {string} threadId 线程ID
     * @param {string} reportId 报告ID
     */
    clearReportCache = (threadId, reportId) => {
        try {
            const LOCAL_STORAGE_KEY = 'grt_report_cache';
            const reportCacheKey = `${threadId}_${reportId}`;

            const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (stored) {
                const cacheData = JSON.parse(stored);
                if (cacheData[reportCacheKey]) {
                    delete cacheData[reportCacheKey];
                    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cacheData));
                }
            }
        } catch (error) {
            console.warn('清理本地缓存失败:', error);
        }
    };

    /**
     * 获取当前活跃的报告ID
     * @returns {string|null} 当前活跃的报告ID
     */
    getActiveReportCardId = () => {
        return this.activeReportCardId;
    };

    /**
     * 设置当前活跃的报告ID
     * @param {string} reportId 报告ID
     */
    setActiveReportCardId = (reportId) => {
        this.activeReportCardId = reportId;
    };

    /**
     * 获取侧边栏打开状态
     * @returns {boolean} 侧边栏是否打开
     */
    getSidebarOpen = () => {
        return this.sidebarOpen;
    };

    /**
     * 设置侧边栏打开状态
     * @param {boolean} open 是否打开
     */
    setSidebarOpen = (open) => {
        this.sidebarOpen = open;
    };

    /**
     * 获取用户手动关闭状态
     * @returns {boolean} 用户是否手动关闭
     */
    getUserManualClosed = () => {
        return this.userManualClosed;
    };

    /**
     * 设置用户手动关闭状态
     * @param {boolean} closed 是否手动关闭
     */
    setUserManualClosed = (closed) => {
        this.userManualClosed = closed;
    };

    /**
     * 获取报告数据引用
     * @returns {Object} 报告数据引用
     */
    getReportDataRef = () => {
        return this.reportDataRef;
    };

    /**
     * 重置报告管理器状态
     */
    reset = () => {
        this.activeReportCardId = null;
        this.sidebarOpen = false;
        this.userManualClosed = false;
        this.reportDataRef = {
            threadId: '',
            reportId: null,
            uniqueReportId: null,
            activityData: '',
            reportContent: '',
            title: '研究报告',
            isFinal: false,
            timestamp: 0
        };
    };
}

/**
 * 创建报告管理器实例
 * @returns {ReportManager} 报告管理器实例
 */
export const createReportManager = () => {
    return new ReportManager();
};

/**
 * 处理会话切换时的侧边栏关闭
 * @param {Function} handleSidebarClose 侧边栏关闭处理函数
 * @param {Function} setActiveReportCardId 设置活跃报告卡片ID的函数
 * @param {Function} originalChangeSession 原始会话切换函数
 * @param {boolean} sidebarOpen 侧边栏是否打开
 * @param {Object} reportData 报告数据
 */
export const handleChangeSessionWithSidebar = (handleSidebarClose, setActiveReportCardId, originalChangeSession, sidebarOpen, reportData) => {
    return (session) => {
        if (sidebarOpen) {
            handleSidebarClose(reportData.reportId);
        }
        // 切换会话时清除当前活跃的报告卡片ID，避免不同会话间的冲突
        setActiveReportCardId(null);
        // 调用原始的changeSession函数
        originalChangeSession(session);
    };
};

/**
 * 处理技能切换时的侧边栏关闭
 * @param {Function} handleSidebarClose 侧边栏关闭处理函数
 * @param {Function} setActiveReportCardId 设置活跃报告卡片ID的函数
 * @param {Function} originalChoseApartSkill 原始技能切换函数
 * @param {boolean} sidebarOpen 侧边栏是否打开
 * @param {Object} reportData 报告数据
 */
export const handleChoseApartSkillWithSidebar = (handleSidebarClose, setActiveReportCardId, originalChoseApartSkill, sidebarOpen, reportData) => {
    return (...args) => {
        if (sidebarOpen) {
            handleSidebarClose(reportData.reportId);
        }
        // 切换技能时清除当前活跃的报告卡片ID
        setActiveReportCardId(null);
        // 调用原始的choseApartSkill函数
        originalChoseApartSkill(...args);
    };
};

/**
 * 处理新消息开始时的侧边栏关闭
 * @param {Function} handleSidebarClose 侧边栏关闭处理函数
 * @param {Function} setActiveReportCardId 设置活跃报告卡片ID的函数
 * @param {Function} originalStartNewMsg 原始新消息开始函数
 * @param {boolean} sidebarOpen 侧边栏是否打开
 * @param {Object} reportData 报告数据
 */
export const handleStartNewMsgWithSidebar = (handleSidebarClose, setActiveReportCardId, originalStartNewMsg, sidebarOpen, reportData) => {
    return (...args) => {
        if (sidebarOpen) {
            handleSidebarClose(reportData.reportId);
        }
        // 开始新会话时清除当前活跃的报告卡片ID
        setActiveReportCardId(null);
        // 调用原始的startNewMsg函数
        originalStartNewMsg(...args);
    };
};

/**
 * 创建报告内容更新处理器
 * @param {Object} context 上下文对象，包含状态和回调函数
 * @returns {Function} 报告内容更新处理器函数
 */
export const createReportContentUpdateHandler = (context) => {
    const {
        activeReportCardId,
        reportDataRef,
        sidebarOpen,
        userManualClosed,
        activeReportId,
        reportNoticeStateUpdaters,
        setGeneratingReports,
        setSidebarOpen,
        setActiveReportId,
        setActiveReportCardId,
        setUserManualClosed,
        setSidebarRenderKey,
        setReportData,
        triggerGlobalPollingCheckRef,
        setActiveGeneratingReportId
    } = context;

    let updateTimer;

    return (event) => {
        const { threadId, reportId, uniqueReportId, activityData, reportData, activityContent, reportContent, title, isFinal, clearPrevious, status, isNew, timestamp } =
            event.detail;

        if (!threadId) {
            return;
        }

        // 使用与消息渲染相同的ID计算逻辑，确保key一致性
        const effectiveReportId = uniqueReportId || reportId;

        // 立即持久化轮询/事件带来的临时更新到 localStorage.generating（兜底）
        // 这样即使后续逻辑因为 UI 状态被过滤，本地存储也能反映最新进度
        try {
            // 优先使用新的字段名，如果没有则使用旧的字段名
            const finalActivityData = activityContent || activityData;
            const finalReportData = reportContent || reportData;

            if (threadId && (finalActivityData || finalReportData || title)) {
                const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
                if (!unifiedData.generating) unifiedData.generating = {};
                const gen = unifiedData.generating[effectiveReportId] || {};
                gen.reportId = effectiveReportId;
                gen.uniqueReportId = effectiveReportId;
                gen.threadId = threadId;
                gen.title = title || gen.title || reportDataRef.current?.title || '研究报告';
                gen.activityContent = finalActivityData || gen.activityContent || reportDataRef.current?.activityContent || '';
                gen.reportContent = finalReportData || gen.reportContent || reportDataRef.current?.reportContent || '';
                gen.timestamp = timestamp || gen.timestamp || Date.now();
                gen.isFinal = !!isFinal || false;
                gen.savedAt = Date.now();
                unifiedData.generating[effectiveReportId] = gen;
                localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));
            }
        } catch (e) {
            console.warn('写入 deerflow_reports_data.generating 失败（兜底）:', e);
        }

        // 检查是否是当前活跃报告
        const isCurrentActiveReport = effectiveReportId === activeReportCardId;

        // 🔥 关键修复：新报告开始的信号必须总是被处理，不受当前活跃报告限制
        const isNewReportStart = clearPrevious && isNew;

        // 在用户手动关闭侧边栏的情况下，我们仍然希望后台轮询/事件能更新本地存储。
        // 如果报告已经被记录在本地存储（completed 或 generating），即使不是当前活跃报告也要处理更新。
        let knownInLocalStorage = false;
        try {
            const unifiedDataCheck = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
            if (unifiedDataCheck && (unifiedDataCheck.completed?.[effectiveReportId] || unifiedDataCheck.generating?.[effectiveReportId])) {
                knownInLocalStorage = true;
            }
        } catch (e) {
            // ignore parse error
            knownInLocalStorage = false;
        }

        // 只有在非新报告开始且非当前活跃报告，并且该报告既不在本地存储也不在当前显示引用时，才过滤事件
        if (!isNewReportStart && !isCurrentActiveReport && !knownInLocalStorage) {
            return;
        }

        // 检查当前reportDataRef是否显示的是不同的报告
        const currentDisplayedReportId = reportDataRef.current?.uniqueReportId || reportDataRef.current?.reportId;
        const isDisplayingDifferentReport = currentDisplayedReportId && currentDisplayedReportId !== effectiveReportId && sidebarOpen;

        // 🔥 关键修复：新报告开始时不受历史报告保护限制
        if (isDisplayingDifferentReport && !isNewReportStart) {
            return; // 直接返回，不处理这个事件
        }

        // 如果收到清空标志，立即重置侧边栏状态
        if (clearPrevious) {
            // 使用小延迟确保卡片DOM已经插入
            setTimeout(() => {
                const forceOpenEvent = new CustomEvent('force-open-report-card', {
                    detail: {
                        reportId: effectiveReportId,
                        uniqueReportId: effectiveReportId,
                        reason: 'new_report_start'
                    }
                });
                window.dispatchEvent(forceOpenEvent);
            }, 50); // 只需要很短的延迟，确保DOM插入完成

            // 重置报告数据
            reportDataRef.current = {
                threadId,
                reportId: effectiveReportId,
                uniqueReportId: effectiveReportId,
                activityData: '',
                reportContent: '',
                title: '研究报告',
                isFinal: false,
                timestamp: timestamp || Date.now()
            };

            triggerGlobalPollingCheckRef.current?.();

            // 立即更新reportData状态，清空旧内容
            setReportData({
                threadId,
                reportId: effectiveReportId,
                uniqueReportId: effectiveReportId,
                activityData: '',
                reportContent: '',
                title: '研究报告'
            });

            // 同时清理本地存储中该报告的生成中数据，避免旧内容被后续合并恢复
            try {
                const unifiedKey = 'deerflow_reports_data';
                const unifiedDataCleanup = JSON.parse(localStorage.getItem(unifiedKey) || '{"completed":{},"generating":{}}');
                if (unifiedDataCleanup && unifiedDataCleanup.generating && unifiedDataCleanup.generating[effectiveReportId]) {
                    delete unifiedDataCleanup.generating[effectiveReportId];
                    localStorage.setItem(unifiedKey, JSON.stringify(unifiedDataCleanup));
                }
            } catch (e) {
                // ignore cleanup error
            }

            // 更新生成状态
            setGeneratingReports((prev) => ({
                ...prev,
                [effectiveReportId]: true
            }));

            // 设置当前正在生成的报告ID
            setActiveGeneratingReportId(effectiveReportId);

            // 清理可能冲突的本地缓存
            clearReportCache(threadId, effectiveReportId);

            // 强制重新渲染侧边栏
            const isViewingHistoricalReport = sidebarOpen && activeReportCardId && activeReportCardId !== effectiveReportId;

            if (!isViewingHistoricalReport) {
                setSidebarRenderKey((prev) => prev + 1);
            }

            // 🔥 修复：新报告开始时的智能切换逻辑
            // 检查用户是否正在查看历史报告
            const isViewingOldReport = sidebarOpen && activeReportId && activeReportId !== effectiveReportId;

            if (isViewingOldReport) {
                // 🔥 关键修复：用户正在查看旧报告时，仍然要让新报告卡片自动打开
                // 但不要强制切换侧边栏内容，保持用户当前查看的旧报告不受干扰

                // 🔥 发送通知事件，让新报告卡片知道自己应该打开
                const newReportEvent = new CustomEvent('new-report-started', {
                    detail: {
                        newReportId: effectiveReportId,
                        currentReportId: activeReportId,
                        threadId: threadId,
                        autoOpenCard: true // 🔥 新增标志，指示新报告卡片应该自动打开
                    }
                });
                window.dispatchEvent(newReportEvent);

                // 使用小延迟确保卡片DOM已经插入
                setTimeout(() => {
                    const forceOpenEvent = new CustomEvent('force-open-report-card', {
                        detail: {
                            reportId: effectiveReportId,
                            uniqueReportId: effectiveReportId,
                            reason: 'new_report_start'
                        }
                    });
                    window.dispatchEvent(forceOpenEvent);
                }, 50); // 只需要很短的延迟，确保DOM插入完成

                // 🔥 继续处理新报告的生成状态更新，但不切换侧边栏
            } else {
                // 🔥 用户没有在查看其他报告，自动切换到新报告
                setActiveReportCardId(effectiveReportId);
                setActiveReportId(effectiveReportId);
                setUserManualClosed(false);
                setSidebarOpen(true);

                // 保存选中的报告ID（对象形式，支持多个报告）
                if (effectiveReportId && threadId) {
                    try {
                        const selectedReports = JSON.parse(localStorage.getItem('selected_report_id') || '{}');
                        selectedReports[threadId] = effectiveReportId;
                        localStorage.setItem('selected_report_id', JSON.stringify(selectedReports));
                    } catch (error) {
                        console.warn('保存选中报告ID失败:', error);
                    }
                }
            }

            return;
        }

        // 处理其他报告内容更新逻辑
        handleReportDataUpdate(
            effectiveReportId,
            {
                threadId,
                reportId: effectiveReportId,
                uniqueReportId: effectiveReportId,
                activityData: activityData || reportDataRef.current.activityData,
                reportContent: reportData || reportDataRef.current.reportContent,
                title: title || reportDataRef.current.title,
                isFinal: !!isFinal,
                timestamp: timestamp || Date.now()
            },
            reportDataRef,
            activeReportCardId
        );

        // 🔥 修复：轮询过程中也更新本地存储，确保进度和标题能被保存
        if (!isFinal && (activityData || reportData || title)) {
            try {
                const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
                if (!unifiedData.generating) unifiedData.generating = {};

                // 更新generating中的报告数据
                unifiedData.generating[effectiveReportId] = {
                    reportId: effectiveReportId,
                    uniqueReportId: effectiveReportId,
                    threadId: threadId,
                    title: title || reportDataRef.current.title || '研究报告',
                    activityContent: activityData || reportDataRef.current.activityData || '',
                    reportContent: reportData || reportDataRef.current.reportContent || '',
                    timestamp: timestamp || Date.now(),
                    isFinal: false,
                    savedAt: Date.now()
                };
                localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));
            } catch (error) {
                console.error('💾 轮询中保存报告到本地存储失败:', error);
            }
        }

        // 如果报告最终化，则设置生成状态为false
        if (isFinal && setGeneratingReports) {
            setGeneratingReports((prev) => {
                const newState = { ...prev };
                const keysToUpdate = [effectiveReportId, reportId, uniqueReportId].filter(Boolean);
                keysToUpdate.forEach((key) => {
                    newState[key] = false;
                });
                return newState;
            });
        }

        // 延迟更新状态，合并多次更新
        clearTimeout(updateTimer);
        updateTimer = setTimeout(() => {
            // 处理侧边栏自动打开逻辑
            // 🔥 修复：新报告开始时，侧边栏必须自动打开，不受userManualClosed影响
            if (isNew === true) {
                // 新报告开始时，强制打开侧边栏
                setSidebarOpen(true);
                setUserManualClosed(false);
            } else if (effectiveReportId === activeReportId && !userManualClosed) {
                // 非新报告的情况下，只有在用户没有手动关闭时才打开
                setSidebarOpen(true);
            }

            // 保存选中的报告ID（对象形式，支持多个报告）
            if (effectiveReportId && threadId) {
                try {
                    const selectedReports = JSON.parse(localStorage.getItem('selected_report_id') || '{}');
                    selectedReports[threadId] = effectiveReportId;
                    localStorage.setItem('selected_report_id', JSON.stringify(selectedReports));
                } catch (error) {
                    console.warn('保存选中报告ID失败:', error);
                }
            }

            // 通知卡片组件更新状态
            if (reportNoticeStateUpdaters && reportNoticeStateUpdaters[`setIsOpen_${effectiveReportId}`]) {
                // 🔥 修复：新报告必须总是自动打开，不受userManualClosed影响
                // 新报告开始时，强制打开报告卡片，无论用户之前是否手动关闭过
                if (isNew === true) {
                    reportNoticeStateUpdaters[`setIsOpen_${effectiveReportId}`](true);
                } else if (!userManualClosed) {
                    // 非新报告的情况下，只有在用户没有手动关闭时才打开
                    reportNoticeStateUpdaters[`setIsOpen_${effectiveReportId}`](true);
                }
            }

            // 如果报告最终化，处理状态更新
            if (isFinal) {
                setGeneratingReports((prev) => {
                    const newState = { ...prev };
                    const keysToUpdate = [effectiveReportId, reportId, uniqueReportId].filter(Boolean);
                    keysToUpdate.forEach((key) => {
                        newState[key] = false;
                    });
                    return newState;
                });

                // 发送状态变化事件
                if (status) {
                    const stateEvent = new CustomEvent('report-state-change', {
                        detail: {
                            reportId: effectiveReportId,
                            isOpen: true,
                            status: status,
                            description: title || reportDataRef.current.title || '研究报告'
                        }
                    });
                    window.dispatchEvent(stateEvent);
                }
            }
        }, 300);
    };
};

/**
 * 处理报告数据更新
 * @param {string} reportId 报告ID
 * @param {Object} data 报告数据
 * @param {Object} reportDataRef 报告数据引用
 * @param {string} activeReportCardId 当前活跃的报告卡片ID
 */
export const handleReportDataUpdate = (reportId, data, reportDataRef, activeReportCardId) => {
    // 检查ID匹配
    if (reportId === activeReportCardId || reportDataRef.current?.uniqueReportId === reportId || reportDataRef.current?.reportId === reportId) {
        reportDataRef.current = {
            ...reportDataRef.current,
            ...data
        };
    }
};

/**
 * 清理报告缓存
 * @param {string} threadId 线程ID
 * @param {string} reportId 报告ID
 */
export const clearReportCache = (threadId, reportId) => {
    try {
        const LOCAL_STORAGE_KEY = 'grt_report_cache';
        const reportCacheKey = `${threadId}_${reportId}`;

        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
            const cacheData = JSON.parse(stored);
            if (cacheData[reportCacheKey]) {
                delete cacheData[reportCacheKey];
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cacheData));
            }
        }
    } catch (error) {
        console.warn('清理本地缓存失败:', error);
    }
};

/**
 * 创建侧边栏打开处理器
 * @param {Object} context 上下文对象
 * @returns {Function} 侧边栏打开处理器函数
 */
export const createSidebarOpenHandler = (context) => {
    const {
        handleSidebarOpenFromFlowData,
        sessionId,
        reportDataRef,
        triggerGlobalPollingCheckRef,
        setSidebarOpen,
        setActiveReportId,
        setActiveReportCardId,
        setUserManualClosed,
        setSidebarRenderKey,
        setReportData
    } = context;

    return async (threadId, reportId, uniqueReportId = null) => {
        // 检查 handleSidebarOpenFromFlowData 是否存在
        if (typeof handleSidebarOpenFromFlowData === 'function') {
            return handleSidebarOpenFromFlowData({
                threadId,
                reportId,
                uniqueReportId,
                sessionId,
                reportDataRef,
                triggerGlobalPollingCheck: triggerGlobalPollingCheckRef.current,
                setSidebarOpen,
                setActiveReportId,
                setActiveReportCardId,
                setUserManualClosed,
                setSidebarRenderKey,
                setReportData
            });
        }

        // 如果 handleSidebarOpenFromFlowData 不存在，使用默认处理逻辑
        console.warn('handleSidebarOpenFromFlowData 未提供，使用默认处理逻辑');

        // 默认处理逻辑
        if (setSidebarOpen) setSidebarOpen(true);
        if (setActiveReportCardId) setActiveReportCardId(uniqueReportId || reportId);
        if (setUserManualClosed) setUserManualClosed(false);
        if (setActiveReportId) setActiveReportId(uniqueReportId || reportId);

        // 保存选中的报告ID到localStorage（对象形式，支持多个报告）
        if ((uniqueReportId || reportId) && threadId) {
            try {
                const selectedReports = JSON.parse(localStorage.getItem('selected_report_id') || '{}');
                selectedReports[threadId] = uniqueReportId || reportId;
                localStorage.setItem('selected_report_id', JSON.stringify(selectedReports));
            } catch (error) {
                console.warn('保存选中报告ID失败:', error);
            }
        }

        return Promise.resolve();
    };
};

/**
 * 创建侧边栏关闭处理器
 * @param {Object} context 上下文对象
 * @returns {Function} 侧边栏关闭处理器函数
 */
export const createSidebarCloseHandler = (context) => {
    const { setSidebarOpen, setUserManualClosed, setActiveReportCardId, reportNoticeStateUpdaters, threadId, reportDataRef } = context;

    return (reportId) => {
        setSidebarOpen(false);
        setUserManualClosed(true);

        // 清除CSS变量，恢复主聊天区域宽度
        document.documentElement.style.removeProperty('--sidebar-space');

        // 清理保存的选中报告ID（对象形式，只清理当前threadId）
        try {
            // 优先使用传入的 threadId，如果没有则从 reportDataRef 获取
            const effectiveThreadId = threadId || (reportDataRef?.current?.threadId);
            if (effectiveThreadId) {
                const selectedReports = JSON.parse(localStorage.getItem('selected_report_id') || '{}');
                delete selectedReports[effectiveThreadId];
                if (Object.keys(selectedReports).length === 0) {
                    localStorage.removeItem('selected_report_id');
                } else {
                    localStorage.setItem('selected_report_id', JSON.stringify(selectedReports));
                }
            } else {
                // 向后兼容：如果没有threadId，清理全部
                localStorage.removeItem('selected_report_id');
            }
        } catch (error) {
            console.warn('清理选中报告ID失败:', error);
        }

        // 清除活跃的报告卡片ID
        setActiveReportCardId(null);

        // 直接更新 deerflow_reports_data 中的状态
        if (reportId) {
            try {
                const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
                const completedReports = unifiedData.completed || {};
                const generatingReports = unifiedData.generating || {};

                // 检查在哪个存储中找到报告
                let foundInCompleted = completedReports[reportId];
                let foundInGenerating = generatingReports[reportId];

                if (foundInCompleted || foundInGenerating) {
                    // 更新报告的打开状态
                    if (foundInCompleted) {
                        completedReports[reportId].isOpen = false;
                    } else if (foundInGenerating) {
                        generatingReports[reportId].isOpen = false;
                    }

                    // 保存回本地存储
                    unifiedData.completed = completedReports;
                    unifiedData.generating = generatingReports;
                    localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));

                    // 触发自定义事件，通知卡片状态变化
                    window.dispatchEvent(
                        new CustomEvent('report-state-change', {
                            detail: {
                                reportId,
                                isOpen: false
                            }
                        })
                    );
                }
            } catch (e) {
                console.error('更新报告状态失败', e);
            }
        }

        // 更新卡片状态
        if (reportId && reportNoticeStateUpdaters) {
            const updateFn = reportNoticeStateUpdaters[`setIsOpen_${reportId}`];
            if (typeof updateFn === 'function') {
                setTimeout(() => {
                    updateFn(false);
                }, 0);
            }
        }
    };
};

/**
 * 创建历史报告完成事件处理器
 * @param {Object} context 上下文对象
 * @returns {Function} 历史报告完成事件处理器函数
 */
export const createReportCompleteFromHistoryHandler = (context) => {
    const { reportDataRef, setGeneratingReports } = context;

    return (event) => {
        const { reportId, uniqueReportId, isFinal } = event.detail;
        const effectiveReportId = uniqueReportId || reportId;

        // 如果报告已完成，更新生成状态
        if (isFinal) {
            setGeneratingReports((prev) => {
                const newState = { ...prev };
                const keysToUpdate = [effectiveReportId, reportId, uniqueReportId].filter(Boolean);
                keysToUpdate.forEach((key) => {
                    newState[key] = false;
                });
                return newState;
            });

            // 如果这是当前reportDataRef中的报告，同时更新isFinal状态
            const currentReportId = reportDataRef.current?.reportId;
            const currentUniqueReportId = reportDataRef.current?.uniqueReportId;
            const currentEffectiveId = currentUniqueReportId || currentReportId;

            if (currentEffectiveId === effectiveReportId) {
                reportDataRef.current.isFinal = true;
            }
        }
    };
};

/**
 * 创建关闭侧边栏事件处理器
 * @param {Function} handleSidebarClose 侧边栏关闭处理函数
 * @returns {Function} 关闭侧边栏事件处理器函数
 */
export const createCloseSidebarEventHandler = (handleSidebarClose) => {
    return (event) => {
        const { reportId } = event.detail;
        handleSidebarClose(reportId);
    };
};

/**
 * 创建报告ID映射更新事件处理器
 * @param {Object} context 上下文对象
 * @returns {Function} 报告ID映射更新事件处理器函数
 */
export const createUpdateReportIdMappingHandler = (context) => {
    const {
        setReportData,
        reportDataRef,
        triggerGlobalPollingCheckRef,
        activeReportId,
        setActiveReportId,
        activeReportCardId,
        setActiveReportCardId,
        sidebarOpen,
        setGeneratingReports,
        deerFlowManager,
        setSidebarRenderKey
    } = context;

    return (event) => {
        const { threadId, temporaryReportId, actualTaskId, uniqueReportId } = event.detail;

        // 更新reportData中的reportId
        setReportData((prev) => {
            if (prev.threadId === threadId && prev.reportId === temporaryReportId) {
                const updatedData = {
                    ...prev,
                    reportId: actualTaskId,
                    uniqueReportId: actualTaskId,
                    taskId: actualTaskId
                };

                // 同时更新reportDataRef
                reportDataRef.current = updatedData;

                // 手动触发全局轮询检查
                triggerGlobalPollingCheckRef.current?.();

                return updatedData;
            }
            return prev;
        });

        // 🔥 修复：更新activeReportId和activeReportCardId，确保ID映射更新时状态正确同步
        if (activeReportId === temporaryReportId) {
            setActiveReportId(actualTaskId);
        }
        if (activeReportCardId === temporaryReportId) {
            setActiveReportCardId(actualTaskId);
        }

        // 🔥 关键修复：如果侧边栏已打开且当前没有选中特定的历史报告卡片，
        // 自动将新报告设为活跃报告，避免显示旧内容
        if (sidebarOpen && (!activeReportCardId || activeReportCardId === temporaryReportId)) {
            setActiveReportCardId(actualTaskId);
        }

        // 更新生成状态映射
        setGeneratingReports((prev) => {
            const newState = { ...prev };
            if (newState[temporaryReportId] !== undefined) {
                newState[actualTaskId] = newState[temporaryReportId];
                delete newState[temporaryReportId];
            }
            return newState;
        });

        // 🔥 关键修复：同步更新deerFlowManager的completedReportsRef
        // 如果旧ID已被标记为完成，需要将新ID也标记为完成
        if (deerFlowManager.isReportCompleted(temporaryReportId)) {
            deerFlowManager.markReportCompleted(actualTaskId, actualTaskId);
        }

        // 强制侧边栏重新渲染
        // 🔥 修复：如果用户正在查看历史报告，不要强制重新渲染
        const isViewingHistoricalReport =
            sidebarOpen && activeReportCardId && activeReportCardId !== actualTaskId && activeReportCardId !== temporaryReportId;

        if (!isViewingHistoricalReport) {
            setSidebarRenderKey((prev) => prev + 1);
        }
    };
};

/**
 * 创建报告数据更新事件处理器
 * @param {Object} context 上下文对象
 * @returns {Function} 报告数据更新事件处理器函数
 */
export const createUpdateReportDataHandler = (context) => {
    const { reportDataRef, sidebarOpen, activeReportCardId, triggerGlobalPollingCheckRef } = context;

    return (event) => {
        const { threadId, reportId, uniqueReportId, activityData, reportContent, title, isFinal, source } = event.detail;

        const effectiveReportId = uniqueReportId || reportId;

        // 🛡️ 区分思考过程和报告内容的保护逻辑
        const currentDisplayedReportId = reportDataRef.current?.uniqueReportId || reportDataRef.current?.reportId;

        // 检查是否需要保护当前显示的历史报告内容
        const shouldProtectContent = sidebarOpen && currentDisplayedReportId && currentDisplayedReportId !== effectiveReportId;
        const shouldProtectActiveReport = sidebarOpen && activeReportCardId && activeReportCardId !== effectiveReportId;

        // 情况1：如果有报告内容更新，且需要保护当前显示的历史报告
        if (reportContent && (shouldProtectContent || shouldProtectActiveReport)) {
            // 完全保护历史报告，包括思考过程
            return;
        }

        // 更新reportDataRef
        reportDataRef.current = {
            ...reportDataRef.current,
            threadId: threadId,
            reportId: reportId,
            uniqueReportId: uniqueReportId,
            activityData: activityData || reportDataRef.current.activityData || '',
            reportContent: reportContent || reportDataRef.current.reportContent || '',
            title: title || reportDataRef.current.title || '研究报告',
            isFinal: isFinal !== undefined ? isFinal : reportDataRef.current.isFinal,
            timestamp: Date.now()
        };

        // 🔥 修复：当报告完成时，即使侧边栏关闭也要更新本地存储
        if (isFinal && (activityData || reportContent)) {
            try {
                const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
                if (!unifiedData.completed) unifiedData.completed = {};
                if (!unifiedData.generating) unifiedData.generating = {};

                const reportDataToSave = {
                    reportId: effectiveReportId,
                    uniqueReportId: effectiveReportId,
                    threadId: threadId,
                    title: title || '研究报告',
                    activityContent: activityData || '',
                    reportContent: reportContent || '',
                    timestamp: Date.now(),
                    isFinal: true,
                    savedAt: Date.now()
                };

                unifiedData.completed[effectiveReportId] = reportDataToSave;
                // 报告完成时，从 generating 中移除
                if (unifiedData.generating[effectiveReportId]) {
                    delete unifiedData.generating[effectiveReportId];
                }
                localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));
            } catch (error) {
                console.error('💾 保存完成的报告到本地存储失败:', error);
            }
        }

        // 手动触发全局轮询检查
        if (triggerGlobalPollingCheckRef.current) {
            triggerGlobalPollingCheckRef.current();
        }
    };
};

/**
 * 创建同步deerFlowManager状态事件处理器
 * @param {Object} context 上下文对象
 * @returns {Function} 同步deerFlowManager状态事件处理器函数
 */
export const createSyncDeerFlowManagerHandler = (context) => {
    const { deerFlowManager } = context;

    return (event) => {
        const { action, reportId, uniqueReportId, reason } = event.detail;

        if (action === 'markCompleted' && reportId) {
            // 标记报告为完成
            deerFlowManager.markReportCompleted(reportId, uniqueReportId);
        }
    };
};

/**
 * 创建所有报告相关事件处理器的集合
 * @param {Object} context 上下文对象
 * @returns {Object} 事件处理器集合
 */
export const createReportEventHandlers = (context) => {
    return {
        handleReportCompleteFromHistory: createReportCompleteFromHistoryHandler(context),
        handleUpdateReportIdMapping: createUpdateReportIdMappingHandler(context),
        handleUpdateReportData: createUpdateReportDataHandler(context),
        handleSyncDeerFlowManager: createSyncDeerFlowManagerHandler(context)
    };
};

/**
 * 注册所有报告相关事件监听器
 * @param {Object} handlers 事件处理器集合
 */
export const registerReportEventListeners = (handlers) => {
    const { handleReportCompleteFromHistory, handleUpdateReportIdMapping, handleUpdateReportData, handleSyncDeerFlowManager } = handlers;

    window.addEventListener('report-complete-from-history', handleReportCompleteFromHistory);
    window.addEventListener('update-report-id-mapping', handleUpdateReportIdMapping);
    window.addEventListener('update-report-data', handleUpdateReportData);
    window.addEventListener('sync-deerflow-manager', handleSyncDeerFlowManager);
};

/**
 * 移除所有报告相关事件监听器
 * @param {Object} handlers 事件处理器集合
 */
export const removeReportEventListeners = (handlers) => {
    const { handleReportCompleteFromHistory, handleUpdateReportIdMapping, handleUpdateReportData, handleSyncDeerFlowManager } = handlers;

    window.removeEventListener('report-complete-from-history', handleReportCompleteFromHistory);
    window.removeEventListener('update-report-id-mapping', handleUpdateReportIdMapping);
    window.removeEventListener('update-report-data', handleUpdateReportData);
    window.removeEventListener('sync-deerflow-manager', handleSyncDeerFlowManager);
};

/**
 * 创建全局轮询管理器
 * @param {Object} dependencies 依赖对象
 * @returns {Function} 全局轮询管理函数
 */
export const createGlobalPollingManager = (dependencies) => {
    const { startDeerFlowGlobalPolling, clearDeerFlowGlobalPolling, deerFlowManager, handleDeerFlowHistory, onHistoryDataUpdate } = dependencies;

    return (reportDataRef, sidebarOpen, activeReportCardId, globalPollingTrigger) => {
        // 监听reportDataRef的变化，启动或停止全局轮询
        const currentThreadId = reportDataRef.current.threadId;
        const currentReportId = reportDataRef.current.reportId;
        const currentUniqueReportId = reportDataRef.current.uniqueReportId;
        const currentIsFinal = reportDataRef.current.isFinal;

        // 🔥 监听flowData中轮询返回的历史数据
        const handleHistoryUpdate = (event) => {
            const { threadId, sessionId, historyData } = event.detail;

            // 🔥 修复：直接调用onHistoryDataUpdate，不再依赖threadId匹配
            // 因为currentThreadId在初始化时可能为空，但我们需要处理所有的历史数据更新
            if (onHistoryDataUpdate && historyData) {
                try {
                    onHistoryDataUpdate(historyData);
                } catch (error) {
                    console.error('🔥 调用onHistoryDataUpdate失败:', error);
                }
            }
        };

        // 添加事件监听器（如果之前没有添加过）
        if (!window.deerflowHistoryListenerAdded) {
            window.addEventListener('deerflow-history-update', handleHistoryUpdate);
            window.deerflowHistoryListenerAdded = true;
        }

        // 🔥 关键修复：检查是否应该启动轮询
        // 只有在以下情况下才启动全局轮询：
        // 1. 侧边栏关闭时
        // 2. 侧边栏打开且显示的是当前reportDataRef中的报告
        const effectiveReportId = currentUniqueReportId || currentReportId;

        const shouldStartPolling = !sidebarOpen || !activeReportCardId || activeReportCardId === effectiveReportId;

        if (!shouldStartPolling) {
            return;
        }

        // 🚀 新增：如果侧边栏显示的是已完成的历史报告，也不启动轮询
        if (sidebarOpen && activeReportCardId && activeReportCardId !== effectiveReportId) {
            const isActiveReportCompleted = deerFlowManager.isReportCompleted(activeReportCardId);
            if (isActiveReportCompleted) {
                return;
            }
        }

        // 如果有threadId和reportId，并且任务未完成，启动轮询
        if (currentThreadId && (currentReportId || currentUniqueReportId) && !currentIsFinal) {
            // 检查是否已标记为完成
            const isCompleted = deerFlowManager.isReportCompleted(effectiveReportId, currentUniqueReportId);

            if (!isCompleted) {
                // 🔥 修复：不再启动全局轮询，避免与flowData.js中的轮询重复
                // DeerFlow的轮询已经在handleDeerFlowChatWithUI中统一管理
            }
        } else if (currentIsFinal) {
            // 如果任务已完成，停止轮询
            if (effectiveReportId) {
                deerFlowManager.markReportCompleted(effectiveReportId, currentUniqueReportId);
                if (deerFlowManager.getActiveReportId() === effectiveReportId) {
                    clearDeerFlowGlobalPolling();
                }
            }
        }
    };
};

/**
 * PDF下载函数
 * @param {string} markdown Markdown内容
 * @param {string} title 文档标题
 * @param {Object} options 额外选项
 */
export const downloadPdf = async (markdown, title, options = {}) => {
    try {
        // 设置下载锁
        isPdfDownloading = true;

        // 检测Safari浏览器
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        // 🔥 优化：使用更优雅的加载提示，Safari用户显示特别提示
        const loadingMsg = message.loading({
            content: isSafari ? '正在生成PDF文件...(Safari用户可能需要稍等片刻)' : '正在生成PDF文件...',
            duration: 0,
            style: {
                marginTop: '20vh' // 调整位置避免遮挡内容
            },
            className: 'pdf-loading-message'
        });

        // 生成PDF
        const pdfBlob = await markdownToPdf(markdown, title, options);

        if (pdfBlob) {
            downloadBlob(pdfBlob, `${title || '研究报告'}.pdf`);
            loadingMsg();
            // 🔥 优化：成功提示更简洁
            message.success({
                content: 'PDF文件下载成功',
                duration: 2,
                style: {
                    marginTop: '20vh'
                }
            });
        } else {
            loadingMsg();
            message.error({
                content: 'PDF生成失败',
                duration: 3,
                style: {
                    marginTop: '20vh'
                }
            });
        }
    } catch (error) {
        console.error('PDF下载失败:', error);
        message.error({
            content: 'PDF下载失败，请重试',
            duration: 3,
            style: {
                marginTop: '20vh'
            }
        });
    } finally {
        // 无论成功还是失败，都要释放锁
        isPdfDownloading = false;
    }
};

/**
 * 增强版Markdown转DOCX转换函数 - 支持更丰富的格式
 * @param {string} markdown Markdown内容
 * @param {string} title 文档标题
 * @param {string} style 样式预设 ('minimal', 'academic', 'business')
 * @param {Object} options 额外选项
 * @returns {Promise<Blob>} DOCX文件Blob
 */
export const markdownToDocxEnhanced = async (markdown, title, style = 'minimal', options = {}) => {
    try {
        if (!markdown || typeof markdown !== 'string') {
            throw new Error('无效的Markdown内容');
        }

        // 样式配置
        const styleConfigs = {
            minimal: {
                fontSize: 24, // 12pt
                fontFamily: 'Arial',
                spacing: { after: 200 }, // 段落间距
                headingColor: '2E86AB',
                codeBackground: 'F8F9FA'
            },
            academic: {
                fontSize: 22, // 11pt
                fontFamily: 'Times New Roman',
                spacing: { after: 240 },
                headingColor: '1F4E79',
                codeBackground: 'F5F5F5'
            },
            business: {
                fontSize: 24, // 12pt
                fontFamily: 'Calibri',
                spacing: { after: 180 },
                headingColor: '0E4B99',
                codeBackground: 'E8F4FD'
            }
        };

        const config = styleConfigs[style] || styleConfigs.minimal;
        const lines = markdown.split('\n');
        const docElements = [];

        // 添加文档标题
        if (title) {
            docElements.push(
                new Paragraph({
                    text: title,
                    heading: HeadingLevel.TITLE,
                    spacing: { after: 400 },
                    alignment: 'center'
                })
            );
        }

        let inCodeBlock = false;
        let codeLines = [];
        let inList = false;
        let listItems = [];
        let inTable = false;
        let tableRows = [];
        let tableHeaders = [];

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const trimmedLine = line.trim();

            // 处理表格
            if (trimmedLine.includes('|') && !inCodeBlock) {
                if (!inTable) {
                    // 开始表格
                    inTable = true;
                    tableRows = [];
                    tableHeaders = [];
                }

                // 跳过分隔线 (例如: |---|---|---|)
                if (trimmedLine.match(/^\|?[\s\-\:\|]*\|?$/) && trimmedLine.includes('-')) {
                    continue;
                }

                // 处理表格行
                let cells;
                if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
                    // 标准格式: |cell1|cell2|cell3|
                    cells = trimmedLine
                        .slice(1, -1)
                        .split('|')
                        .map((cell) => cell.trim());
                } else {
                    // 简化格式: cell1|cell2|cell3
                    cells = trimmedLine.split('|').map((cell) => cell.trim());
                }

                // 过滤空单元格（除非是有意的空单元格）
                if (cells.length > 0 && cells.some((cell) => cell !== '')) {
                    if (tableHeaders.length === 0) {
                        tableHeaders = cells;
                    } else {
                        // 确保数据行和表头列数一致
                        while (cells.length < tableHeaders.length) {
                            cells.push('');
                        }
                        if (cells.length > tableHeaders.length) {
                            cells = cells.slice(0, tableHeaders.length);
                        }
                        tableRows.push(cells);
                    }
                }
                continue;
            } else if (inTable && trimmedLine === '') {
                // 表格结束，生成表格元素
                if (tableHeaders.length > 0) {
                    try {
                        // 创建表格
                        const table = new Table({
                            rows: [
                                // 表头行
                                new TableRow({
                                    children: tableHeaders.map(
                                        (header) =>
                                            new TableCell({
                                                children: [
                                                    new Paragraph({
                                                        text: header,
                                                        run: {
                                                            bold: true,
                                                            color: '1F4E79'
                                                        },
                                                        alignment: 'center'
                                                    })
                                                ],
                                                shading: { fill: 'F2F6FF' },
                                                margins: { top: 150, bottom: 150, left: 200, right: 200 },
                                                verticalAlign: 'center'
                                            })
                                    ),
                                    tableHeader: true
                                }),
                                // 数据行
                                ...tableRows.map(
                                    (row, rowIndex) =>
                                        new TableRow({
                                            children: row.map(
                                                (cell) =>
                                                    new TableCell({
                                                        children: [
                                                            new Paragraph({
                                                                text: cell || '',
                                                                alignment: 'left'
                                                            })
                                                        ],
                                                        margins: { top: 120, bottom: 120, left: 200, right: 200 },
                                                        shading: rowIndex % 2 === 0 ? { fill: 'FFFFFF' } : { fill: 'F8F9FA' },
                                                        verticalAlign: 'center'
                                                    })
                                            )
                                        })
                                )
                            ],
                            width: {
                                size: 100,
                                type: 'pct'
                            },
                            borders: {
                                top: { style: 'single', size: 6, color: '1F4E79' },
                                bottom: { style: 'single', size: 4, color: '1F4E79' },
                                left: { style: 'single', size: 4, color: '1F4E79' },
                                right: { style: 'single', size: 4, color: '1F4E79' },
                                insideHorizontal: { style: 'single', size: 2, color: 'E1E8F0' },
                                insideVertical: { style: 'single', size: 2, color: 'E1E8F0' }
                            },
                            layout: 'autofit'
                        });

                        docElements.push(table);
                        docElements.push(new Paragraph({ text: '', spacing: { after: 200 } })); // 添加间距
                    } catch (error) {
                        console.warn('表格创建失败，回退到文本格式:', error);
                        // 回退：将表格内容作为段落输出
                        if (tableHeaders.length > 0) {
                            docElements.push(new Paragraph({ text: tableHeaders.join(' | '), run: { bold: true } }));
                        }
                        tableRows.forEach((row) => {
                            docElements.push(new Paragraph({ text: row.join(' | ') }));
                        });
                    }
                }

                inTable = false;
                tableRows = [];
                tableHeaders = [];
                continue;
            } else if (inTable) {
                // 在表格内但不是表格行，可能是表格内容的延续
                continue;
            }

            // 处理代码块
            if (trimmedLine.startsWith('```')) {
                if (inCodeBlock) {
                    // 结束代码块
                    if (codeLines.length > 0) {
                        docElements.push(
                            new Paragraph({
                                text: codeLines.join('\n'),
                                style: 'CodeBlock',
                                spacing: config.spacing
                            })
                        );
                    }
                    codeLines = [];
                    inCodeBlock = false;
                } else {
                    // 开始代码块
                    inCodeBlock = true;
                }
                continue;
            }

            if (inCodeBlock) {
                codeLines.push(line);
                continue;
            }

            // 处理列表项
            if (trimmedLine.match(/^[-*+]\s+/)) {
                const listText = trimmedLine.replace(/^[-*+]\s+/, '');
                listItems.push(listText);
                inList = true;
                continue;
            } else if (inList && trimmedLine === '') {
                // 列表结束
                listItems.forEach((item, index) => {
                    docElements.push(
                        new Paragraph({
                            text: `• ${item}`,
                            spacing: { after: 120 },
                            indent: { left: 400 }
                        })
                    );
                });
                listItems = [];
                inList = false;
                continue;
            } else if (inList && !trimmedLine.match(/^[-*+]\s+/)) {
                // 列表结束
                listItems.forEach((item, index) => {
                    docElements.push(
                        new Paragraph({
                            text: `• ${item}`,
                            spacing: { after: 120 },
                            indent: { left: 400 }
                        })
                    );
                });
                listItems = [];
                inList = false;
            }

            // 处理标题
            if (trimmedLine.startsWith('# ')) {
                docElements.push(
                    new Paragraph({
                        text: trimmedLine.replace('# ', ''),
                        heading: HeadingLevel.HEADING_1,
                        spacing: { before: 240, after: 180 }
                    })
                );
            } else if (trimmedLine.startsWith('## ')) {
                docElements.push(
                    new Paragraph({
                        text: trimmedLine.replace('## ', ''),
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 200, after: 140 }
                    })
                );
            } else if (trimmedLine.startsWith('### ')) {
                docElements.push(
                    new Paragraph({
                        text: trimmedLine.replace('### ', ''),
                        heading: HeadingLevel.HEADING_3,
                        spacing: { before: 160, after: 120 }
                    })
                );
            } else if (trimmedLine.startsWith('#### ')) {
                docElements.push(
                    new Paragraph({
                        text: trimmedLine.replace('#### ', ''),
                        heading: HeadingLevel.HEADING_4,
                        spacing: { before: 140, after: 100 }
                    })
                );
            } else if (trimmedLine !== '') {
                // 处理普通段落
                let text = trimmedLine;

                // 🔥 特殊处理：AI生成内容提示右对齐
                if (text.includes('(注：文档包含AI生成内容，请注意鉴别)')) {
                    docElements.push(
                        new Paragraph({
                            text: text,
                            spacing: config.spacing,
                            alignment: 'right',
                            run: {
                                italics: true,
                                color: '666666',
                                size: 20 // 稍小的字体
                            }
                        })
                    );
                } else {
                    // 处理图片：将图片Markdown语法转换为文本说明
                    const imageRegex = /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g;
                    text = text.replace(imageRegex, (match, alt, url, title) => {
                        let imageText = `[图片: ${alt || '图片'}]`;
                        if (title) {
                            imageText += ` - ${title}`;
                        }
                        imageText += ` (原始链接: ${url})`;
                        return imageText;
                    });

                    // 简单处理粗体和斜体（基础版本）
                    text = text.replace(/\*\*(.*?)\*\*/g, '$1'); // 移除粗体标记
                    text = text.replace(/\*(.*?)\*/g, '$1'); // 移除斜体标记
                    text = text.replace(/`(.*?)`/g, '$1'); // 移除行内代码标记

                    docElements.push(
                        new Paragraph({
                            text: text,
                            spacing: config.spacing
                        })
                    );
                }
            } else {
                // 空行
                docElements.push(
                    new Paragraph({
                        text: '',
                        spacing: { after: 120 }
                    })
                );
            }
        }

        // 处理剩余的列表项
        if (listItems.length > 0) {
            listItems.forEach((item) => {
                docElements.push(
                    new Paragraph({
                        text: `• ${item}`,
                        spacing: { after: 120 },
                        indent: { left: 400 }
                    })
                );
            });
        }

        // 处理剩余的表格
        if (inTable && tableHeaders.length > 0) {
            try {
                // 创建表格
                const table = new Table({
                    rows: [
                        // 表头行
                        new TableRow({
                            children: tableHeaders.map(
                                (header) =>
                                    new TableCell({
                                        children: [
                                            new Paragraph({
                                                text: header,
                                                run: {
                                                    bold: true,
                                                    color: '1F4E79'
                                                },
                                                alignment: 'center'
                                            })
                                        ],
                                        shading: { fill: 'F2F6FF' },
                                        margins: { top: 150, bottom: 150, left: 200, right: 200 },
                                        verticalAlign: 'center'
                                    })
                            ),
                            tableHeader: true
                        }),
                        // 数据行
                        ...tableRows.map(
                            (row, rowIndex) =>
                                new TableRow({
                                    children: row.map(
                                        (cell) =>
                                            new TableCell({
                                                children: [
                                                    new Paragraph({
                                                        text: cell || '',
                                                        alignment: 'left'
                                                    })
                                                ],
                                                margins: { top: 120, bottom: 120, left: 200, right: 200 },
                                                shading: rowIndex % 2 === 0 ? { fill: 'FFFFFF' } : { fill: 'F8F9FA' },
                                                verticalAlign: 'center'
                                            })
                                    )
                                })
                        )
                    ],
                    width: {
                        size: 100,
                        type: 'pct'
                    },
                    borders: {
                        top: { style: 'single', size: 6, color: '1F4E79' },
                        bottom: { style: 'single', size: 4, color: '1F4E79' },
                        left: { style: 'single', size: 4, color: '1F4E79' },
                        right: { style: 'single', size: 4, color: '1F4E79' },
                        insideHorizontal: { style: 'single', size: 2, color: 'E1E8F0' },
                        insideVertical: { style: 'single', size: 2, color: 'E1E8F0' }
                    },
                    layout: 'autofit'
                });

                docElements.push(table);
            } catch (error) {
                console.warn('表格创建失败，回退到文本格式:', error);
                // 回退：将表格内容作为段落输出
                if (tableHeaders.length > 0) {
                    docElements.push(new Paragraph({ text: tableHeaders.join(' | '), run: { bold: true } }));
                }
                tableRows.forEach((row) => {
                    docElements.push(new Paragraph({ text: row.join(' | ') }));
                });
            }
        }

        // 创建文档
        const doc = new Document({
            sections: [
                {
                    properties: {
                        page: {
                            margin: {
                                top: 1440, // 1 inch
                                bottom: 1440,
                                left: 1440,
                                right: 1440
                            }
                        }
                    },
                    children: docElements
                }
            ]
        });

        // 打包文档为blob
        return await Packer.toBlob(doc);
    } catch (error) {
        console.error('增强版Markdown转DOCX失败:', error);
        message.error('生成DOCX文件失败');
        throw error;
    }
};

/**
 * DOCX下载助手函数 - 类似于PDF的前端插件下载
 * @param {string} markdown Markdown内容
 * @param {string} title 文档标题
 * @param {string} style 样式预设
 * @param {Object} options 额外选项
 */
export const downloadDocxWithStyle = async (markdown, title, style = 'minimal', options = {}) => {
    try {
        const loadingMsg = message.loading('正在生成DOCX文件...', 0);

        // 直接使用原始Markdown，不转换图片为Base64
        const processedMarkdown = markdown;

        const docxBlob = await markdownToDocxEnhanced(processedMarkdown, title, style, options);

        if (docxBlob) {
            downloadBlob(docxBlob, `${title || '研究报告'}.docx`);
            loadingMsg();
            message.success('DOCX文件下载成功');
        } else {
            loadingMsg();
            message.error('DOCX文件生成失败');
        }
    } catch (error) {
        console.error('DOCX下载失败:', error);
        message.error('DOCX下载失败，请重试');
    }
}
